import { useState, useEffect, useMemo, useRef, useCallback, Fragment } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useRegisterUnsavedChanges } from "../../contexts/UnsavedChangesContext";
import { useToast } from "../../contexts/ToastContext";
import { calendarApi } from "../../store/services/calendarApi";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectClinicProfile,
  selectCanAccessFinance,
  selectAllowMultipleTherapists,
  selectIsAdmin,
  selectPunchCardsEnabled,
  selectIsEndMeetingSmsEnabled,
  selectTempCustomersEnabled,
  selectEnglishCommunicationEnabled,
} from "../../store/slices/authSlice";
import { useGetActivePunchCardQuery } from "../../store/services/punchCardsApi";
import {
  getPatientTherapistIds,
  getTransactionByAppointment,
  syncConfirmationLinkPhone,
  subscribeToAppointmentSmsState,
} from "../../firebase";
import DeleteAppointmentDialog from "./DeleteAppointmentDialog";
import { sortPatientsByName } from "../../utils/patientSortUtils";
import {
  showGlobalLoader,
  hideGlobalLoader,
  selectOnboardingActive,
} from "../../store/slices/uiSlice";
import usePlanPermissions from "../../hooks/usePlanPermissions";
import {
  useCreateAppointmentMutation,
  useUpdateAppointmentMutation,
  useUncancelAppointmentMutation,
  useGetAppointmentQuery,
  useDeleteAppointmentMutation,
  useCreateConfirmationLinkMutation,
  useDeactivateConfirmationLinkMutation,
  useCancelScheduledSmsMutation,
  useGetConfirmationLinksQuery,
  useCreateRecurringAppointmentsMutation,
  useDeleteRecurringAppointmentsMutation,
  useUpdateRecurringAppointmentsMutation,
  useGetRecurringGroupAppointmentsQuery,
  useGetAppointmentsQuery,
  useGetSmsSettingsQuery,
  useGetWeeklyAvailabilityQuery,
  RECURRING_FREQUENCY,
  SERIES_SLOT,
  calculateSeriesOccurrences,
} from "../../store/services/calendarApi";
import { useGetCategoriesQuery } from "../../store/services/tasksApi";
import { useGetClinicTherapistsQuery } from "../../store/services/therapistApi";
import {
  useGetRoomsQuery,
} from "../../store/services/roomsApi";
import {
  formatLocalDateString,
  formatHebrewDate as formatHebrewDateUtil,
  HEBREW_DAYS_FULL,
  getWeekId,
} from "../../utils/dateUtils";
import {
  sendAppointmentSMS,
  formatHebrewDate,
  checkRoomAvailability as checkRoomAvailabilityFn,
  isWithinRoomAvailabilityHours,
} from "../../firebase";
import {
  X,
  Trash,
  CheckCircle,
  Check,
  Clock,
  Warning,
  Stethoscope,
  CurrencyCircleDollar,
  Repeat,
  CalendarBlank,
  User,
  Door,
  UserCircle,
  Calendar,
  NotePencil,
  ChatCircleText,
  Phone,
  ArrowRight,
  Link,
  Copy,
  Eye,
  ArrowsClockwise,
  CalendarPlus,
  Info,
  UserPlus,
  CalendarX,
  ListNumbers,
  Question,
  PaperPlaneTilt,
  Receipt,
  CaretDown,
  CaretUp,
  SlidersHorizontal,
  Ticket,
} from "@phosphor-icons/react";
import ConfirmDialog from "../ui/ConfirmDialog";
import TimePicker from "../ui/TimePicker";
import DatePicker from "../ui/DatePicker";
import { HelpButton } from "../shared";
import ModalCloseButton from "../ui/ModalCloseButton";
import LoaderLoop from "../shared/loader/LoaderLoop";
import CustomClinikitActionButton from "../ui/CustomClinikitActionButton";
import PhoneInput from "../ui/PhoneInput";
import useCancelAppointmentFlow from "../dashboard/hooks/useCancelAppointmentFlow";
import { getHolidayForDateStr } from "../../utils/israeliHolidays";
import {
  SMS_SUPPRESS_REASON,
  applySmsScheduleGuards,
  buildConfirmationSmsText,
  buildSeriesSmsMessage as buildSeriesSmsMessagePure,
  buildSmsPreview as buildSmsPreviewPure,
  getFrequencyLabel,
  getSchedulingFromPreview,
} from "../../utils/appointmentSmsPreview";
import { getPatientLanguage } from "../../utils/patientLanguage";
import {
  getRateStep,
  validatePatient,
  validateSchedule,
  validateDetails,
  validateExternalConflicts,
} from "../../utils/appointmentFormRules";
import "./AppointmentModal.css";

// Sentinel value for the "temp customer" (לקוח מזדמן) option in the patient select
const TEMP_CUSTOMER_OPTION = "__temp_customer__";

// Weekday options for the second slot of a twice-weekly series
const WEEKDAY_OPTIONS = HEBREW_DAYS_FULL.map((label, value) => ({ value, label }));

const TX_PAID_FAMILY = new Set([
  "paid",
  "invoice_manual",
  "invoice_auto",
]);

/** Hebrew label for a transaction status — mirrors the cancel-flow copy. */
function describeTxStatus(tx) {
  if (!tx) return "";
  switch (tx.status) {
    case "paid":
      return "שולם";
    case "invoice_manual":
      return "שולם + חשבונית";
    case "invoice_auto":
      return "שולם + חשבונית (אוטומטי)";
    case "pending":
    case "pending_collection":
      return "ממתין לתשלום";
    case "expected":
      return "צפי הכנסה";
    case "refunded":
      return "זוכה";
    case "cancelled":
      return "בוטל";
    default:
      return tx.status || "";
  }
}

/** ₪-formatted amount for inline display. */
function formatTxAmount(value) {
  const numeric = parseFloat(value);
  if (!Number.isFinite(numeric)) return "";
  return `₪${numeric.toLocaleString("he-IL")}`;
}

function AppointmentModal({
  appointment,
  prefilledDate,
  prefilledDateOnly = false,
  patients,
  onClose,
  targetTherapistId = null,
  targetTherapistName = null,
  scheduledByAdminId = null,
  scheduledByAdminName = null,
  preSelectedRoomId = null,
  onSuccess = null,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const clinicId = useSelector(selectClinicId);
  const currentTherapistId = useSelector(selectTherapistId);
  const currentTherapistName = useSelector(selectTherapistName);
  const clinicProfile = useSelector(selectClinicProfile);
  const englishCommunicationEnabled = useSelector(
    selectEnglishCommunicationEnabled
  );
  const canAccessFinance = useSelector(selectCanAccessFinance);
  const allowMultipleTherapists = useSelector(selectAllowMultipleTherapists);
  const isAdmin = useSelector(selectIsAdmin);
  const isEndMeetingSmsEnabled = useSelector(selectIsEndMeetingSmsEnabled);
  const isOnboardingActive = useSelector(selectOnboardingActive);
  const isEditing = !!appointment;

  // Toast notifications
  const { showSuccess, showError } = useToast();

  // Plan permissions hook for SMS limit check
  const {
    checkSMS,
    refreshUsageCache,
    PlanLimitModalComponent,
  } = usePlanPermissions();

  const isAdminScheduling = !!targetTherapistId;
  const [appointmentTherapistId, setAppointmentTherapistId] = useState("");

  // Fetch clinic therapists when:
  // - Admin scheduling mode (create-as-admin), OR
  // - Multi-therapist clinic (any user, for per-occurrence therapist override in series), OR
  // - Editing an existing appointment as admin in a multi-therapist clinic (needed for the
  //   series-overview "מטפל/ת" column + reassignment dropdown)
  const { data: clinicTherapists = [] } = useGetClinicTherapistsQuery(
    { clinicId },
    {
      skip:
        !clinicId ||
        (!isAdminScheduling &&
          !allowMultipleTherapists &&
          !(isEditing && isAdmin)),
    }
  );

  // In admin scheduling mode the "responsible therapist" is fixed by
  // `targetTherapistId` (passed in by the caller — e.g. the slot click in
  // ClinicCalendarModal pre-selects the admin themself or the first active
  // therapist; TherapistCalendarModal pre-selects the therapist whose calendar
  // is being viewed). The admin can still override the therapist
  // per-appointment via the multi-therapist branch when the patient is
  // assigned to multiple therapists, exactly like in the regular popup.
  // EDIT: anchor to the appointment's original therapist (see useAppointmentForm).
  const baseTherapistId =
    (isEditing && appointment?.therapistId) ||
    targetTherapistId ||
    currentTherapistId;
  const baseTherapistName =
    (isEditing && appointment?.therapistId && appointment?.therapistName) ||
    targetTherapistName ||
    currentTherapistName;

  const effectivePatients = useMemo(() => sortPatientsByName(patients), [patients]);

  const defaultDuration = clinicProfile?.defaultMeetingTime || 45;
  const defaultRate = clinicProfile?.defaultHourlyRate || "";

  const [createAppointment, { isLoading: isCreating }] = useCreateAppointmentMutation();
  const [updateAppointment, { isLoading: isUpdating }] = useUpdateAppointmentMutation();
  const [uncancelAppointment, { isLoading: isRestoringMutation }] = useUncancelAppointmentMutation();
  const [deleteAppointment, { isLoading: isDeleting }] = useDeleteAppointmentMutation();
  const [createConfirmationLink, { isLoading: isCreatingLink }] = useCreateConfirmationLinkMutation();
  const [deactivateConfirmationLink] = useDeactivateConfirmationLinkMutation();
  const [cancelScheduledSms] = useCancelScheduledSmsMutation();
  const [createRecurringAppointments, { isLoading: isCreatingRecurring }] = useCreateRecurringAppointmentsMutation();
  const [deleteRecurringAppointments, { isLoading: isDeletingRecurring }] = useDeleteRecurringAppointmentsMutation();
  const [updateRecurringAppointments, { isLoading: isUpdatingRecurring }] = useUpdateRecurringAppointmentsMutation();

  // Shared cancellation flow (modal). Wired to the orange "בטל פגישה" button
  // in the footer. We intentionally do NOT close the AppointmentModal in
  // `onCancelled` — the flow still has a "record cancellation fee" follow-up
  // step (see `CANCEL_STEPS.FEE_ASK`), and closing the parent modal here would
  // unmount `cancelFlowNode` mid-flow. The appointment modal will refresh to
  // show the "cancelled" banner automatically and the user closes it manually
  // (or the flow's own close button) when done.
  // Postponing a series from the cancel flow rewrites this meeting and its
  // siblings — the series table has to catch up.
  const { startCancel, startPostpone, cancelFlowNode } = useCancelAppointmentFlow({
    onCancelled: () => refetchSeries(),
    onPostponed: (result) => {
      refetchSeries();
      // The form still holds the pre-postpone slot — move it with the meeting
      // so the modal doesn't contradict what was just saved.
      if (result?.newStartTime) {
        const moved = new Date(result.newStartTime);
        setFormData((prev) => ({
          ...prev,
          date: formatLocalDateString(moved),
          startTime: moved.toTimeString().slice(0, 5),
        }));
      }
    },
  });
  const { data: confirmationLinks = [], refetch: refetchLinks } =
    useGetConfirmationLinksQuery(
      { clinicId, appointmentId: appointment?.id },
      { skip: !isEditing || !appointment?.id, refetchOnMountOrArgChange: true }
    );

  const { data: seriesAppointments = [], refetch: refetchSeries } = useGetRecurringGroupAppointmentsQuery(
    { clinicId, recurringGroupId: appointment?.recurringGroupId, currentTherapistId, isAdmin },
    {
      skip: !isEditing || !appointment?.recurringGroupId,
      // Reopening the modal must never show a stale series table — a cancel /
      // reschedule done elsewhere (calendar, carousel, another occurrence's
      // modal) may have landed while this query had no subscribers.
      refetchOnMountOrArgChange: true,
    }
  );

  // Live copy of the edited appointment. The `appointment` prop is a snapshot
  // the parent captured on click (calendarSlice / local state) — it never
  // updates when the RTK cache invalidates, so status-driven UI (cancelled
  // banner, footer delete/cancel buttons) must read the subscribed doc or a
  // restore would leave a stale "בוטלה" banner on screen.
  const { data: liveFetchedAppointment } = useGetAppointmentQuery(
    { clinicId, appointmentId: appointment?.id },
    { skip: !clinicId || !appointment?.id }
  );
  const liveAppointment = liveFetchedAppointment || appointment;

  // Restore a cancelled appointment back into its slot (and its series, if it
  // belongs to one — cancelling never strips the recurring fields). Serves
  // both the cancelled banner and the per-row restore in the series overview
  // table; `restoringRowId` disables just the clicked control while working.
  const [restoringRowId, setRestoringRowId] = useState(null);
  const handleRestoreAppointment = useCallback(async (targetApt) => {
    if (!targetApt?.id || restoringRowId) return;
    setRestoringRowId(targetApt.id);
    try {
      await uncancelAppointment({
        clinicId,
        appointmentId: targetApt.id,
        patientId: targetApt.patientId,
        patientName: targetApt.patientName,
        therapistId: currentTherapistId,
        therapistName: currentTherapistName,
      }).unwrap();
      showSuccess("הפגישה שוחזרה בהצלחה");
      refetchSeries();
    } catch (error) {
      console.error("[CK-3419] Error restoring cancelled appointment:", {
        errorCode: "CK-3419",
        errorMessage: error?.message,
        appointmentId: targetApt?.id,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשחזור הפגישה");
    } finally {
      setRestoringRowId(null);
    }
  }, [
    restoringRowId,
    uncancelAppointment,
    clinicId,
    currentTherapistId,
    currentTherapistName,
    showSuccess,
    showError,
    refetchSeries,
  ]);
  const isRestoring = restoringRowId === appointment?.id && isRestoringMutation;

  const { data: treatmentTypes = [] } = useGetCategoriesQuery(
    { clinicId, type: "treatmentType" },
    { skip: !clinicId }
  );

  const { data: clinicRooms = [] } = useGetRoomsQuery(
    { clinicId },
    { skip: !clinicId }
  );

  // Fetched in edit mode too — the late "send SMS now" flow needs
  // sendReminderEnabled / reminderHoursBefore / includeTreatmentTypeInSms.
  const { data: therapistSmsSettings } = useGetSmsSettingsQuery(
    { therapistId: baseTherapistId },
    { skip: !baseTherapistId }
  );

  const roomsEnabled = clinicRooms.some((room) => room.isActive !== false);

  const accessibleRooms = useMemo(() => {
    if (isAdminScheduling && !isEditing) {
      return clinicRooms.filter((room) => room.isActive !== false);
    }
    return clinicRooms.filter(
      (room) =>
        room.isActive !== false &&
        (room.allowOthersToBook === true ||
          room.defaultTherapistId === baseTherapistId)
    );
  }, [clinicRooms, baseTherapistId, isAdminScheduling, isEditing]);

  const defaultRoomForTherapist = useMemo(() => {
    return clinicRooms.find(
      (room) =>
        room.isActive !== false && room.defaultTherapistId === baseTherapistId
    );
  }, [clinicRooms, baseTherapistId]);

  const [formData, setFormData] = useState({
    patientId: "",
    patientName: "",
    isTempCustomer: false,
    tempCustomerPhone: "",
    date: "",
    startTime: "",
    duration: defaultDuration,
    notes: "",
    sendSms: true,
    sendEndMeetingSms: true,
    treatmentTypeId: "",
    paymentAmount: defaultRate ? defaultRate.toString() : "",
    isRecurring: false,
    recurringFrequency: RECURRING_FREQUENCY.WEEKLY,
    recurringEndDate: "",
    recurringSecondDay: "",
    recurringSecondTime: "",
    seriesTotalCount: 10,
    seriesStartIndex: 1,
    roomId: "",
  });

  // Tracks where the current rate value came from: "patient" | "treatmentType" | "clinic" | "manual" | "punchCard" | null
  const [rateSource, setRateSource] = useState(null);

  // Communication language for this patient's SMS and confirmation page.
  // Hebrew unless the clinic opted in AND this patient is flagged English;
  // temp customers have no patient doc, so they stay Hebrew.
  const smsLanguage = useMemo(
    () =>
      getPatientLanguage(
        effectivePatients.find((p) => p.id === formData.patientId) || null,
        englishCommunicationEnabled
      ),
    [effectivePatients, formData.patientId, englishCommunicationEnabled]
  );

  // ===== Temp customers (לקוחות מזדמנים) =====
  const tempCustomersEnabled = useSelector(selectTempCustomersEnabled);

  // ===== Punch card (כרטיסיה) =====
  const punchCardsEnabled = useSelector(selectPunchCardsEnabled);
  const [usePunchCard, setUsePunchCard] = useState(false);
  // Remembers the rate before checking "מתוך כרטיסיה" so unchecking restores it
  const prevRateBeforePunchRef = useRef(null);

  const { data: activePunchCard = null } = useGetActivePunchCardQuery(
    { clinicId, patientId: formData.patientId },
    {
      skip:
        !punchCardsEnabled ||
        !canAccessFinance ||
        !clinicId ||
        !formData.patientId,
    }
  );

  const remainingPunches = activePunchCard
    ? Math.max(
        0,
        (activePunchCard.totalVisits || 0) - (activePunchCard.usedVisits || 0)
      )
    : 0;
  const punchAmountForCard = activePunchCard
    ? activePunchCard.paidUpfront === true &&
      activePunchCard.purchaseIncomeRecorded === true
      ? 0
      : activePunchCard.pricePerVisit || 0
    : 0;

  // Checkbox visibility: clinic feature on + finance access + patient picked.
  // Create: only when the card has remaining punches.
  // Edit: only single (non-recurring), non-cancelled appointments — either to
  // un-punch an already punched meeting or to punch one with remaining visits.
  const showPunchOption =
    punchCardsEnabled &&
    canAccessFinance &&
    !!formData.patientId &&
    (!isEditing
      ? remainingPunches > 0
      : !appointment?.isRecurring &&
        appointment?.status !== "cancelled" &&
        (appointment?.isPunchCard === true || remainingPunches > 0));

  // The rate input is driven by the punch card (and therefore read-only) for a
  // single punched meeting. In *series* mode the input keeps the regular rate —
  // it applies to the occurrences the card does not cover — so it stays live.
  // Shared by the rate field and by the missing-rate gate below.
  const rateDisabled = usePunchCard && (isEditing || !formData.isRecurring);

  // A new meeting with no rate (empty or 0) needs an explicit confirmation.
  // Skipped while the guided tour is running (same as the past-date gate) and
  // for therapists who cannot see the rate field at all.
  const needsAmountGate =
    !isEditing &&
    !isOnboardingActive &&
    canAccessFinance &&
    !rateDisabled &&
    !(parseFloat(formData.paymentAmount) > 0);

  // Recompute the rate via the normal priority chain (patient > treatment
  // type > clinic default) — used when un-punching an appointment whose
  // pre-punch rate we don't know.
  const applyDefaultRatePriority = useCallback(() => {
    const selectedPatient = effectivePatients.find(
      (p) => p.id === formData.patientId
    );
    let newRate = defaultRate ? defaultRate.toString() : "";
    let newSource = defaultRate ? "clinic" : null;
    const tt = treatmentTypes.find((t) => t.id === formData.treatmentTypeId);
    if (tt?.rate != null) {
      newRate = tt.rate.toString();
      newSource = "treatmentType";
    }
    if (selectedPatient?.treatmentRate) {
      newRate = selectedPatient.treatmentRate.toString();
      newSource = "patient";
    }
    setFormData((prev) => ({ ...prev, paymentAmount: newRate }));
    setRateSource(newSource);
  }, [effectivePatients, formData.patientId, formData.treatmentTypeId, treatmentTypes, defaultRate]);

  const handlePunchToggle = (checked) => {
    setUsePunchCard(checked);

    // In series-creation mode the rate input keeps the regular rate (it
    // applies to the meetings NOT covered by the card); punched meetings get
    // the card's per-visit terms automatically.
    if (formData.isRecurring && !isEditing) return;

    if (checked && (activePunchCard || appointment?.isPunchCard)) {
      prevRateBeforePunchRef.current = {
        amount: formData.paymentAmount,
        source: rateSource,
      };
      if (activePunchCard) {
        setFormData((prev) => ({
          ...prev,
          paymentAmount: punchAmountForCard.toString(),
        }));
      }
      setRateSource("punchCard");
    } else if (!checked) {
      if (prevRateBeforePunchRef.current) {
        const prev = prevRateBeforePunchRef.current;
        prevRateBeforePunchRef.current = null;
        setFormData((p) => ({ ...p, paymentAmount: prev.amount }));
        setRateSource(prev.source);
      } else {
        // Un-punching an appointment opened already punched
        applyDefaultRatePriority();
      }
    }
  };

  // Editing an already punched appointment opens with the checkbox on
  useEffect(() => {
    if (isEditing) {
      const punched = appointment?.isPunchCard === true;
      setUsePunchCard(punched);
      if (punched) setRateSource("punchCard");
    }
  }, [isEditing, appointment?.id, appointment?.isPunchCard]);

  // Reset the punch checkbox when switching patient (the card is per-patient)
  const punchPatientRef = useRef(formData.patientId);
  useEffect(() => {
    if (punchPatientRef.current !== formData.patientId) {
      punchPatientRef.current = formData.patientId;
      if (!isEditing && usePunchCard) {
        setUsePunchCard(false);
        prevRateBeforePunchRef.current = null;
      }
    }
  }, [formData.patientId, isEditing, usePunchCard]);

  // Toggling series mode while "מתוך כרטיסיה" is checked: series mode keeps
  // the regular rate in the input, single mode shows the card's per-visit rate
  useEffect(() => {
    if (isEditing || !usePunchCard || !activePunchCard) return;
    if (formData.isRecurring) {
      if (prevRateBeforePunchRef.current) {
        const prev = prevRateBeforePunchRef.current;
        prevRateBeforePunchRef.current = null;
        setFormData((p) => ({ ...p, paymentAmount: prev.amount }));
        setRateSource(prev.source);
      }
    } else if (rateSource !== "punchCard") {
      prevRateBeforePunchRef.current = {
        amount: formData.paymentAmount,
        source: rateSource,
      };
      setFormData((p) => ({
        ...p,
        paymentAmount: punchAmountForCard.toString(),
      }));
      setRateSource("punchCard");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.isRecurring]);

  const [errors, setErrors] = useState({});
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [smsError, setSmsError] = useState(null);
  const [smsSending, setSmsSending] = useState(false);
  // Late "send confirmation SMS now" dialog (edit mode, no-SMS appointments)
  const [showLateSmsDialog, setShowLateSmsDialog] = useState(false);
  const [lateSmsScope, setLateSmsScope] = useState("single");
  const [lateSmsIncludeReminder, setLateSmsIncludeReminder] = useState(false);
  const [lateSmsSending, setLateSmsSending] = useState(false);
  // Live SMS scheduling state for the open appointment. The Cloud Function
  // rewrites the send times / task names a second or two AFTER a save, so the
  // RTK Query snapshot the modal was opened with (and the refetch that fires
  // right after the save) can still hold the pre-trigger values. This listener
  // keeps the "מתוזמן" times honest without a page refresh.
  const [liveSmsState, setLiveSmsState] = useState(null);
  // Cancel-a-queued-SMS flow: { type: "initial" | "reminder" } while confirming
  const [cancelSmsTarget, setCancelSmsTarget] = useState(null);
  const [cancellingSmsType, setCancellingSmsType] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteScope, setDeleteScope] = useState("single");
  const [showRecurringDeleteDialog, setShowRecurringDeleteDialog] = useState(false);
  // Single-appointment delete is "in flight" — distinct from RTK's
  // `isDeleting` so the dialog can stay disabled across the close-and-toast
  // tail of the success path.
  const [deleteDialogWorking, setDeleteDialogWorking] = useState(false);
  // For recurring deletes, the payment shown is for the CURRENT appointment.
  // We also expose an explicit "include past" toggle so the therapist can opt
  // into deleting historical siblings (default off — preserves the previous
  // skip-past behaviour for series).
  const [recurringDeleteState, setRecurringDeleteState] = useState({
    linkedTransaction: undefined,
    preloadError: null,
    deleteLinkedTransactions: false,
    includePast: false,
    isWorking: false,
  });
  const [showRecurringEditDialog, setShowRecurringEditDialog] = useState(false);
  const [editScope, setEditScope] = useState("single");
  const pendingEditDataRef = useRef(null);
  // Set when re-submitting a series edit that the therapist chose to push
  // through a conflict — skips re-asking for the scope.
  const reuseEditScopeRef = useRef(false);
  // The scope dialog serves two flows: the main form and a series-overview row
  // being rescheduled. Non-null means the dialog is answering for that row.
  const [rowScopeEdit, setRowScopeEdit] = useState(null);
  const recurringConfirmInFlightRef = useRef(false);
  const [showSeriesOverview, setShowSeriesOverview] = useState(
    !!(appointment?.isRecurring && appointment?.recurringGroupId)
  );
  const [seriesOverrides, setSeriesOverrides] = useState({});
  const [editingSeriesRow, setEditingSeriesRow] = useState(null);
  const [editingOverviewRow, setEditingOverviewRow] = useState(null);
  const [overviewEditValues, setOverviewEditValues] = useState({});
  // Confirm-dialog state when saving a series-overview row whose appointment
  // already happened (past date or completed/cancelled status). Holds the
  // payload that's pending so the dialog can confirm-and-save.
  const [pastReassignConfirm, setPastReassignConfirm] = useState({
    open: false,
    apt: null,
  });
  const [recurringConflicts, setRecurringConflicts] = useState([]);
  // Set by the "צור בכל זאת" button on the conflict panel — the therapist saw
  // the exact list of colliding meetings and chose to double-book anyway.
  // Reset on every new conflict result and whenever the dates change, so the
  // consent always refers to the list that was actually on screen.
  const allowRecurringConflictsRef = useRef(false);
  const conflictPanelRef = useRef(null);
  const [initialFormData, setInitialFormData] = useState(null);
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  const [showOverlapConfirm, setShowOverlapConfirm] = useState(false);
  const [overlappingAppointment, setOverlappingAppointment] = useState(null);
  const [pastDateTimeError, setPastDateTimeError] = useState("");
  const [isPastDateTime, setIsPastDateTime] = useState(false);
  const [showPastDateConfirm, setShowPastDateConfirm] = useState(false);
  const pastDateConfirmedRef = useRef(false);
  const [overlapConfirmed, setOverlapConfirmed] = useState(false);
  const overlapConfirmedRef = useRef(false);
  const debounceTimerRef = useRef(null);
  const [roomConflicts, setRoomConflicts] = useState([]);
  const [roomAvailabilityError, setRoomAvailabilityError] = useState(null);
  const [roomCheckPending, setRoomCheckPending] = useState(false);
  const [showNoRoomConfirm, setShowNoRoomConfirm] = useState(false);
  const [noRoomConfirmed, setNoRoomConfirmed] = useState(false);
  const noRoomConfirmedRef = useRef(false);
  // Missing-rate gate: a meeting created without an amount never gets a
  // financial transaction (calendarApi only creates one when paymentAmount > 0),
  // so it silently vanishes from collection, expected income and reports.
  const [showZeroAmountConfirm, setShowZeroAmountConfirm] = useState(false);
  const [zeroAmountInput, setZeroAmountInput] = useState("");
  const [zeroAmountError, setZeroAmountError] = useState("");
  const zeroAmountConfirmedRef = useRef(false);
  // Rate typed inside the gate popup. Read at write time because
  // `resumeAfterGate` re-enters the submit through a stale closure, so the
  // fresh `formData` is not visible there.
  const zeroAmountOverrideRef = useRef(null);
  // Records which action opened a gate popup ("preview" | "submit") so its
  // confirm handler resumes the flow that triggered it — Preview must never
  // fall through to immediate creation.
  const gateOriginRef = useRef("submit");
  // Scrollable modal body — used to auto-scroll to the first invalid field.
  const modalContentRef = useRef(null);
  const [showSmsPreview, setShowSmsPreview] = useState(false);
  const [smsPreviewItems, setSmsPreviewItems] = useState([]);
  // Collapsed-by-default "advanced" disclosure for per-message SMS controls.
  const [showSmsDetails, setShowSmsDetails] = useState(false);
  // Per-occurrence cancel prompt for the "series overview" table (X button).
  // We intentionally keep this a lightweight inline dialog instead of the full
  // `useCancelAppointmentFlow` (which asks about cancellation fees / invoice)
  // so admins can rapidly triage an entire series while still being able to
  // capture an optional reason string.
  const [seriesCancelPrompt, setSeriesCancelPrompt] = useState({
    open: false,
    aptId: null,
    reason: "",
    isWorking: false,
  });

  // Multi-therapist: compute selected patient's therapist IDs for per-appointment dropdown
  const selectedPatientForMulti = useMemo(() => {
    if (!allowMultipleTherapists || !formData.patientId) return null;
    return effectivePatients.find((p) => p.id === formData.patientId);
  }, [allowMultipleTherapists, formData.patientId, effectivePatients]);

  const selectedPatientTherapistIds = useMemo(() => {
    if (!selectedPatientForMulti) return [];
    return getPatientTherapistIds(selectedPatientForMulti);
  }, [selectedPatientForMulti]);

  const isMultiTherapistPatient = selectedPatientTherapistIds.length > 1;

  // Admin-only ability to reassign the responsible therapist for a single
  // occurrence inside a recurring series, from the series-overview table.
  // We intentionally keep this gated to admins (clinic owner / admin role)
  // because non-admin therapists can only see their own occurrences in the
  // series (`getRecurringGroupAppointments` filters by therapistId), so
  // reassigning would make the row disappear from their own list.
  const canReassignSeriesTherapist =
    isEditing && isAdmin && allowMultipleTherapists && isMultiTherapistPatient;

  // Final therapistId/therapistName — applies multi-therapist override for single appointments
  const therapistId =
    appointmentTherapistId && !formData.isRecurring && isMultiTherapistPatient
      ? appointmentTherapistId
      : baseTherapistId;

  const therapistName = useMemo(() => {
    if (appointmentTherapistId && !formData.isRecurring && isMultiTherapistPatient) {
      const t = clinicTherapists.find((ct) => ct.uid === appointmentTherapistId);
      if (t) return `${t.firstName} ${t.lastName}`;
    }
    return baseTherapistName;
  }, [appointmentTherapistId, formData.isRecurring, isMultiTherapistPatient, clinicTherapists, baseTherapistName]);

  const appointmentDate = formData.date ? new Date(formData.date) : null;
  const { data: dayAppointments = [] } = useGetAppointmentsQuery(
    {
      clinicId,
      startDate: appointmentDate
        ? new Date(appointmentDate.setHours(0, 0, 0, 0)).toISOString()
        : null,
      endDate: appointmentDate
        ? new Date(
            new Date(formData.date).setHours(23, 59, 59, 999)
          ).toISOString()
        : null,
      therapistId,
    },
    { skip: !clinicId || !formData.date || !therapistId }
  );

  // Therapist's weekly availability doc for the selected date — used only to
  // warn (never block) when the appointment overlaps a blocked-time window.
  const apptWeekId = formData.date ? getWeekId(new Date(formData.date)) : null;
  const { data: blockWeekAvailability } = useGetWeeklyAvailabilityQuery(
    { therapistId, weekId: apptWeekId },
    { skip: !therapistId || !apptWeekId }
  );

  // Minute-precision overlap between the selected time and a blocked window.
  const overlappingBlock = useMemo(() => {
    if (!formData.date || !formData.startTime) return null;
    const dayBlocked =
      blockWeekAvailability?.slots?.find((d) => d.date === formData.date)
        ?.blocked || [];
    if (!dayBlocked.length) return null;
    const [h, m] = formData.startTime.split(":").map(Number);
    const startMin = h * 60 + m;
    const endMin = startMin + (parseInt(formData.duration, 10) || 45);
    return (
      dayBlocked.find((b) => {
        const [bh, bm] = b.start.split(":").map(Number);
        const [eh, em] = b.end.split(":").map(Number);
        return startMin < eh * 60 + em && bh * 60 + bm < endMin;
      }) || null
    );
  }, [blockWeekAvailability, formData.date, formData.startTime, formData.duration]);

  useEffect(() => {
    if (appointment) {
      const startDate = new Date(appointment.startTime);
      setFormData({
        patientId: appointment.patientId || "",
        patientName: appointment.patientName || "",
        isTempCustomer: appointment.isTempCustomer === true,
        tempCustomerPhone: appointment.tempCustomerPhone || "",
        date: formatLocalDateString(startDate),
        startTime: startDate.toTimeString().slice(0, 5),
        duration: appointment.duration || defaultDuration,
        notes: appointment.notes || "",
        sendSms: false,
        // Keep the stored opt-in/out (see useAppointmentForm hydrate)
        sendEndMeetingSms: appointment.sendEndMeetingSms === true,
        treatmentTypeId: appointment.treatmentTypeId || "",
        paymentAmount: appointment.paymentAmount?.toString() || "",
        isRecurring: appointment.isRecurring || false,
        recurringFrequency:
          appointment.recurringFrequency || RECURRING_FREQUENCY.WEEKLY,
        recurringEndDate: appointment.recurringEndDate
          ? formatLocalDateString(new Date(appointment.recurringEndDate))
          : "",
        roomId: appointment.roomId || "",
      });
    } else if (prefilledDate) {
      const defaultEndDate = new Date(prefilledDate);
      defaultEndDate.setDate(defaultEndDate.getDate() + 56);

      if (prefilledDateOnly) {
        setFormData((prev) => ({
          ...prev,
          date: formatLocalDateString(prefilledDate),
          duration: defaultDuration,
          paymentAmount: defaultRate ? defaultRate.toString() : prev.paymentAmount,
          recurringEndDate: formatLocalDateString(defaultEndDate),
        }));
      } else {
        const timeToUse = isOnboardingActive 
          ? new Date().toTimeString().slice(0, 5)
          : prefilledDate.toTimeString().slice(0, 5);
        const dateToUse = isOnboardingActive
          ? formatLocalDateString(new Date())
          : formatLocalDateString(prefilledDate);

        setFormData((prev) => ({
          ...prev,
          date: dateToUse,
          startTime: timeToUse,
          duration: defaultDuration,
          paymentAmount: defaultRate ? defaultRate.toString() : prev.paymentAmount,
          recurringEndDate: formatLocalDateString(defaultEndDate),
        }));
      }
      if (defaultRate) setRateSource("clinic");
    } else {
      const now = new Date();
      const defaultEndDate = new Date(now);
      defaultEndDate.setDate(defaultEndDate.getDate() + 56);

      setFormData((prev) => ({
        ...prev,
        date: formatLocalDateString(now),
        duration: defaultDuration,
        paymentAmount: defaultRate ? defaultRate.toString() : prev.paymentAmount,
        recurringEndDate: formatLocalDateString(defaultEndDate),
      }));
      if (defaultRate) setRateSource("clinic");
    }
  }, [appointment, prefilledDate, prefilledDateOnly, defaultDuration, defaultRate, isOnboardingActive]);

  useEffect(() => {
    if (!isOnboardingActive || isEditing) return;
    const now = new Date();
    setFormData((prev) => ({
      ...prev,
      date: formatLocalDateString(now),
      startTime: now.toTimeString().slice(0, 5),
    }));
  }, [isOnboardingActive, isEditing]);

  // When opening an existing appointment that belongs to a recurring series,
  // expand the "סדרת מפגשים" overview by default so the therapist immediately
  // sees the full series context (siblings, indices, status) instead of having
  // to click the toggle.
  useEffect(() => {
    if (appointment?.isRecurring && appointment?.recurringGroupId) {
      setShowSeriesOverview(true);
    }
  }, [appointment?.id, appointment?.isRecurring, appointment?.recurringGroupId]);

  useEffect(() => {
    if (!isEditing && !formData.roomId) {
      if (preSelectedRoomId) {
        setFormData((prev) => ({ ...prev, roomId: preSelectedRoomId }));
      } else if (defaultRoomForTherapist) {
        setFormData((prev) => ({ ...prev, roomId: defaultRoomForTherapist.id }));
      }
    }
  }, [isEditing, defaultRoomForTherapist, formData.roomId, preSelectedRoomId]);

  useEffect(() => {
    if (initialFormData === null) {
      const timeoutId = setTimeout(() => {
        setInitialFormData({ ...formData });
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [formData, initialFormData]);

  const isFormDirty = useMemo(() => {
    if (!initialFormData) return false;
    if (!isEditing) {
      return (
        formData.patientId !== "" ||
        formData.isTempCustomer === true ||
        formData.notes !== "" ||
        formData.treatmentTypeId !== "" ||
        formData.paymentAmount !== (defaultRate ? defaultRate.toString() : "")
      );
    }
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  }, [formData, initialFormData, isEditing, defaultRate]);

  useRegisterUnsavedChanges(isFormDirty);

  const handleSafeClose = useCallback(() => {
    if (isFormDirty) {
      setShowCloseWarning(true);
    } else {
      onClose();
    }
  }, [isFormDirty, onClose]);

  const handleConfirmClose = useCallback(() => {
    setShowCloseWarning(false);
    onClose();
  }, [onClose]);

  const checkAvailability = useCallback(() => {
    setPastDateTimeError("");
    setOverlappingAppointment(null);
    setOverlapConfirmed(false);
    overlapConfirmedRef.current = false;
    gateOriginRef.current = "submit";
    // Reset past date confirmation when date/time changes
    pastDateConfirmedRef.current = false;

    if (!formData.date || !formData.startTime) {
      setIsPastDateTime(false);
      return;
    }

    const appointmentDateTime = new Date(`${formData.date}T${formData.startTime}`);
    const now = new Date();

    if (appointmentDateTime < now && !isEditing && !isOnboardingActive) {
      setIsPastDateTime(true);
    } else {
      setIsPastDateTime(false);
    }

    if (dayAppointments.length > 0 && formData.duration) {
      const newStart = appointmentDateTime.getTime();
      const newEnd = newStart + parseInt(formData.duration, 10) * 60000;

      const overlapping = dayAppointments.find((appt) => {
        if (isEditing && appt.id === appointment?.id) return false;
        const existingStart = new Date(appt.startTime).getTime();
        const existingEnd = new Date(appt.endTime).getTime();
        return newStart < existingEnd && newEnd > existingStart;
      });

      if (overlapping) {
        setOverlappingAppointment(overlapping);
      }
    }
  }, [formData.date, formData.startTime, formData.duration, dayAppointments, isEditing, appointment?.id]);

  const checkRoomAvailability = useCallback(async () => {
    setRoomConflicts([]);
    setRoomAvailabilityError(null);

    if (!formData.roomId || !formData.date || !formData.startTime || !formData.duration) {
      return;
    }

    setRoomCheckPending(true);
    try {
      const startDateTime = new Date(`${formData.date}T${formData.startTime}`);
      const endDateTime = new Date(startDateTime.getTime() + parseInt(formData.duration, 10) * 60000);

      const availabilityHoursResult = await isWithinRoomAvailabilityHours(
        clinicId,
        formData.roomId,
        startDateTime,
        endDateTime
      );

      if (!availabilityHoursResult.isWithinAvailability) {
        setRoomAvailabilityError(availabilityHoursResult.message);
        setRoomCheckPending(false);
        return;
      }

      const result = await checkRoomAvailabilityFn(
        clinicId,
        formData.roomId,
        startDateTime,
        endDateTime,
        isEditing ? appointment?.id : null
      );

      if (!result.isAvailable) {
        setRoomConflicts(result.conflicts);
      }
    } catch (error) {
      console.error("Error checking room availability:", error);
    } finally {
      setRoomCheckPending(false);
    }
  }, [formData.roomId, formData.date, formData.startTime, formData.duration, clinicId, isEditing, appointment?.id]);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      checkAvailability();
      if (roomsEnabled && formData.roomId) {
        checkRoomAvailability();
      }
    }, 800);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [checkAvailability, checkRoomAvailability, roomsEnabled, formData.roomId]);

  // Auto-disable SMS when past date is detected, re-enable when future date
  // Respects therapist settings: sendOnCreate AND sendReminderEnabled
  useEffect(() => {
    if (!isEditing) {
      if (isPastDateTime) {
        setFormData((prev) => ({
          ...prev,
          sendSms: false,
          sendEndMeetingSms: false,
        }));
      } else if (!isOnboardingActive) {
        // Check SMS by default if EITHER message is enabled in settings.
        const smsDefault = therapistSmsSettings?.sendOnCreate !== false || therapistSmsSettings?.sendReminderEnabled !== false;
        setFormData((prev) => ({
          ...prev,
          sendSms: smsDefault,
          // End-meeting message defaults on when the clinic feature is enabled.
          sendEndMeetingSms: isEndMeetingSmsEnabled,
        }));
      }
    }
  }, [isPastDateTime, isEditing, isOnboardingActive, isEndMeetingSmsEnabled, therapistSmsSettings?.sendOnCreate, therapistSmsSettings?.sendReminderEnabled]);

  const seriesPreview = useMemo(() => {
    if (!formData.isRecurring || !formData.date || isEditing) {
      return { dates: [], count: 0, items: [] };
    }
    const totalCount = parseInt(formData.seriesTotalCount, 10) || 0;
    const startIndex = parseInt(formData.seriesStartIndex, 10) || 1;
    if (totalCount <= 0 || startIndex < 1 || startIndex > totalCount) {
      return { dates: [], count: 0, items: [] };
    }
    const startDate = new Date(`${formData.date}T${formData.startTime || "09:00"}`);
    if (isNaN(startDate.getTime())) {
      return { dates: [], count: 0, items: [] };
    }
    const isTwiceWeekly = formData.recurringFrequency === RECURRING_FREQUENCY.TWICE_WEEKLY;
    const secondSlot =
      isTwiceWeekly && formData.recurringSecondDay !== "" && formData.recurringSecondTime
        ? { dayOfWeek: Number(formData.recurringSecondDay), time: formData.recurringSecondTime }
        : null;

    // A twice-weekly series needs its second slot before it can be previewed
    if (isTwiceWeekly && !secondSlot) {
      return { dates: [], count: 0, items: [] };
    }

    const baseOccurrences = calculateSeriesOccurrences(
      startDate,
      formData.recurringFrequency,
      totalCount,
      startIndex,
      secondSlot
    );
    const baseTime = formData.startTime || "09:00";

    const items = baseOccurrences.map(({ date, slot }, index) => {
      const key = index;
      const override = seriesOverrides[key];
      let finalDate = new Date(date);
      let finalTime = slot === SERIES_SLOT.SECONDARY ? formData.recurringSecondTime : baseTime;

      if (override?.date) {
        const [y, m, d] = override.date.split('-').map(Number);
        finalDate.setFullYear(y, m - 1, d);
      }
      if (override?.time) {
        finalTime = override.time;
        const [h, min] = override.time.split(':').map(Number);
        finalDate.setHours(h, min, 0, 0);
      }

      return {
        date: finalDate,
        time: finalTime,
        dateStr: override?.date || formatLocalDateString(date),
        seriesIndex: startIndex + index,
        slot,
        isOverridden: !!override,
      };
    });

    return {
      dates: items.map(i => i.date),
      items,
      count: baseOccurrences.length,
      totalCount,
      startIndex,
    };
  }, [formData.isRecurring, formData.date, formData.startTime, formData.seriesTotalCount, formData.seriesStartIndex, formData.recurringFrequency, formData.recurringSecondDay, formData.recurringSecondTime, isEditing, seriesOverrides]);

  // Editing a single meeting's date/time in the series table invalidates the
  // conflict result that was computed for the previous dates — drop it (and any
  // "create anyway" consent) so the next submit re-checks against the server.
  useEffect(() => {
    setRecurringConflicts((prev) => (prev.length === 0 ? prev : []));
    allowRecurringConflictsRef.current = false;
  }, [seriesOverrides]);

  // Same for the edited meeting itself: picking another date/time invalidates a
  // refused series shift, so drop the conflicts, the waiver and the pending
  // edit payload instead of leaving a stale one behind.
  useEffect(() => {
    if (!isEditing) return;
    setRecurringConflicts((prev) => (prev.length === 0 ? prev : []));
    allowRecurringConflictsRef.current = false;
    reuseEditScopeRef.current = false;
    pendingEditDataRef.current = null;
  }, [isEditing, formData.date, formData.startTime, formData.duration]);

  const handlePatientChange = (e) => {
    const patientId = e.target.value;

    // Temp customer (לקוח מזדמן) - no patient record; therapist types name+phone
    if (patientId === TEMP_CUSTOMER_OPTION) {
      setFormData((prev) => ({
        ...prev,
        patientId: "",
        patientName: "",
        isTempCustomer: true,
        tempCustomerPhone: "",
        duration: defaultDuration,
        paymentAmount: defaultRate ? defaultRate.toString() : "",
      }));
      setRateSource(defaultRate ? "clinic" : null);
      setErrors((prev) => {
        const { patientId: _, ...rest } = prev;
        return rest;
      });
      return;
    }

    const selectedPatient = effectivePatients.find((p) => p.id === patientId);

    // Rate priority: patient > treatmentType > clinic default
    let newRate = defaultRate ? defaultRate.toString() : "";
    let newRateSource = defaultRate ? "clinic" : null;
    let newTreatmentTypeId = "";

    if (selectedPatient) {
      // Auto-fill treatment type from patient default.
      // NOTE: this legacy modal only renders during the onboarding tour. The
      // treatment-type duration chain (patient → type → clinic) lives in the
      // wizard (appointmentWizard/useAppointmentForm.js) and is intentionally
      // not mirrored here.
      if (selectedPatient.defaultTreatmentTypeId) {
        const patientDefaultType = treatmentTypes.find(t => t.id === selectedPatient.defaultTreatmentTypeId);
        if (patientDefaultType) {
          newTreatmentTypeId = patientDefaultType.id;
          if (patientDefaultType.rate != null) {
            newRate = patientDefaultType.rate.toString();
            newRateSource = "treatmentType";
          }
        }
      }

      // Patient rate always wins if set
      if (selectedPatient.treatmentRate) {
        newRate = selectedPatient.treatmentRate.toString();
        newRateSource = "patient";
      }
    }

    setFormData((prev) => ({
      ...prev,
      patientId,
      patientName: selectedPatient ? `${selectedPatient.firstName} ${selectedPatient.lastName}` : "",
      isTempCustomer: false,
      tempCustomerPhone: "",
      duration: selectedPatient?.treatmentDuration || defaultDuration,
      paymentAmount: newRate,
      treatmentTypeId: newTreatmentTypeId || prev.treatmentTypeId,
    }));
    setRateSource(newRateSource);

    // Multi-therapist: default appointment therapist to the effective scheduler.
    // When an admin is scheduling on behalf of another therapist (e.g. from
    // the clinic-wide calendar), the "scheduler" is the target therapist
    // pre-selected by the caller (`targetTherapistId`) — NOT the logged-in
    // admin. Without this, selecting a multi-therapist patient would silently
    // overwrite the admin's choice with the admin's own uid (or the first
    // therapist on the patient), and the final `therapistId` computation
    // (line ~342) would then assign the appointment to the wrong therapist.
    if (allowMultipleTherapists && selectedPatient) {
      const pTids = getPatientTherapistIds(selectedPatient);
      if (pTids.length > 1) {
        const schedulerTherapistId = baseTherapistId;
        setAppointmentTherapistId(
          pTids.includes(schedulerTherapistId) ? schedulerTherapistId : pTids[0]
        );
      } else {
        setAppointmentTherapistId("");
      }
    }

    if (patientId) {
      setErrors((prev) => {
        const { patientId: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "treatmentTypeId") {
      const selectedType = treatmentTypes.find(t => t.id === value);
      const selectedPatient = effectivePatients.find(p => p.id === formData.patientId);
      const patientHasRate = selectedPatient?.treatmentRate;

      // Only update rate if patient doesn't have a personal rate
      if (!patientHasRate) {
        if (selectedType?.rate != null) {
          setFormData((prev) => ({
            ...prev,
            treatmentTypeId: value,
            paymentAmount: selectedType.rate.toString(),
          }));
          setRateSource("treatmentType");
        } else {
          setFormData((prev) => ({
            ...prev,
            treatmentTypeId: value,
            paymentAmount: defaultRate ? defaultRate.toString() : "",
          }));
          setRateSource(defaultRate ? "clinic" : null);
        }
      } else {
        setFormData((prev) => ({ ...prev, treatmentTypeId: value }));
      }
      return;
    }

    if (name === "paymentAmount") {
      setFormData((prev) => ({ ...prev, paymentAmount: value }));
      setRateSource("manual");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (["date", "startTime", "duration", "recurringEndDate", "recurringFrequency", "seriesTotalCount", "seriesStartIndex", "recurringSecondDay", "recurringSecondTime"].includes(name)) {
      clearRecurringConflicts();
      setSeriesOverrides({});
      setEditingSeriesRow(null);
    }
    if (["date", "startTime", "duration", "roomId"].includes(name)) {
      setRoomConflicts([]);
      setRoomAvailabilityError(null);
    }
    if (name === "roomId" && value) {
      noRoomConfirmedRef.current = false;
      setNoRoomConfirmed(false);
    }
  };

  /**
   * Steps the rate up (+1) or down (-1). Step size comes from the current
   * value: multiples of 5 jump by 25, anything else moves by 1.
   * Mirrors handleChange's paymentAmount branch so the rate-source hint
   * flips to "manual" exactly as it does when the therapist types.
   */
  const adjustPaymentAmount = useCallback((direction) => {
    setFormData((prev) => {
      const current = parseFloat(prev.paymentAmount);
      const base = Number.isFinite(current) ? current : 0;
      const step = getRateStep(prev.paymentAmount);
      // Round to 2 decimals: stepping a value like 237.5 must not drift into
      // float noise (238.50000000000003) inside the input.
      const next = Math.max(
        0,
        Math.round((base + direction * step) * 100) / 100
      );
      if (next === base && prev.paymentAmount !== "") return prev;
      return { ...prev, paymentAmount: String(next) };
    });
    setRateSource("manual");
  }, []);

  // Arrow keys must follow the same 25/1 rule as the buttons — the native
  // number input would otherwise step by its own default of 1.
  const handlePaymentAmountKeyDown = useCallback(
    (e) => {
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
      e.preventDefault();
      adjustPaymentAmount(e.key === "ArrowUp" ? 1 : -1);
    },
    [adjustPaymentAmount]
  );

  // The scope dialog answers for either the main form or a series-overview row.
  const scopeApt = rowScopeEdit?.apt || appointment;
  const scopeNewStart = rowScopeEdit
    ? new Date(rowScopeEdit.updateData.startTime)
    : formData.date && formData.startTime
    ? new Date(`${formData.date}T${formData.startTime}`)
    : null;
  const scopeNewDateStr = scopeNewStart
    ? formatLocalDateString(scopeNewStart)
    : null;

  // "יום שלישי, 12/08/2025 בשעה 16:00" — used across the scope options so the
  // therapist reads the actual result instead of an abstract rule.
  const formatScopeWhen = (date, { withDate = true } = {}) => {
    if (!date || Number.isNaN(date.getTime())) return "";
    const time = `${String(date.getHours()).padStart(2, "0")}:${String(
      date.getMinutes()
    ).padStart(2, "0")}`;
    const dayName = `יום ${HEBREW_DAYS_FULL[date.getDay()]}`;
    return withDate
      ? `${dayName}, ${formatHebrewDateUtil(date)} בשעה ${time}`
      : `${dayName} בשעה ${time}`;
  };

  // "Push the rest by a week" only makes sense when the meeting actually moved
  // to another DAY — a same-day time change keeps the two classic options.
  const editDateChanged = Boolean(
    isEditing &&
      scopeApt?.startTime &&
      scopeNewDateStr &&
      formatLocalDateString(new Date(scopeApt.startTime)) !== scopeNewDateStr
  );

  // Occurrences after the edited one that a "this + future" edit would move —
  // the server's `future` scope skips only cancelled/deleted siblings.
  const laterSeriesMeetings = (() => {
    if (!isEditing || !scopeApt?.startTime) return [];
    const originalStartMs = new Date(scopeApt.startTime).getTime();
    return seriesAppointments.filter((apt) => {
      if (apt.id === scopeApt.id) return false;
      if (apt.isDeleted === true) return false;
      if (apt.status === "cancelled") return false;
      return new Date(apt.startTime).getTime() > originalStartMs;
    });
  })();

  // A twice-weekly series has two independent slots and the server only shifts
  // the edited one, so the count must not include the other slot's meetings.
  const laterShiftedMeetings = (() => {
    if (scopeApt?.recurringFrequency !== RECURRING_FREQUENCY.TWICE_WEEKLY) {
      return laterSeriesMeetings;
    }
    const targetSlot = scopeApt?.recurringSlot || null;
    const targetDay = scopeApt?.startTime
      ? new Date(scopeApt.startTime).getDay()
      : null;
    return laterSeriesMeetings.filter((apt) =>
      apt.recurringSlot && targetSlot
        ? apt.recurringSlot === targetSlot
        : new Date(apt.startTime).getDay() === targetDay
    );
  })();

  // The occurrences a postpone would actually move — same filter the server
  // applies. Nothing left to push means the option is pointless.
  const postponableMeetings = laterSeriesMeetings.filter(
    (apt) =>
      apt.status !== "completed" && new Date(apt.startTime).getTime() > Date.now()
  );

  const canPostponeSeries = editDateChanged && postponableMeetings.length > 0;

  // Postponing this meeting a week is only an option while it is still ahead
  // of us — used by the delete dialog's "push it instead" shortcut.
  const canPostponeAppointment = (() => {
    if (!appointment?.isRecurring || !appointment.startTime) return false;
    const moved = new Date(appointment.startTime);
    moved.setDate(moved.getDate() + 7);
    return moved.getTime() > Date.now();
  })();

  // Preview: where the NEXT meeting lands once the rest of the series slides a
  // week — the concrete day/time the therapist cares about most.
  const postponedNextMeetingDate = canPostponeSeries
    ? (() => {
        const next = new Date(
          Math.min(
            ...postponableMeetings.map((apt) => new Date(apt.startTime).getTime())
          )
        );
        next.setDate(next.getDate() + 7);
        return next;
      })()
    : null;

  // Preview: where the last meeting of the series ends up once every later
  // occurrence slides a week.
  const postponedLastMeetingDate = canPostponeSeries
    ? (() => {
        const lastStart = new Date(
          Math.max(
            ...postponableMeetings.map((apt) => new Date(apt.startTime).getTime())
          )
        );
        lastStart.setDate(lastStart.getDate() + 7);
        return lastStart;
      })()
    : null;

  // Moving the date back onto the original day retires the postpone option —
  // don't leave it selected behind the therapist's back.
  useEffect(() => {
    if (!canPostponeSeries && editScope === "postpone_week") {
      setEditScope("single");
    }
  }, [canPostponeSeries, editScope]);

  // A series that collides with itself (two of its own meetings overlapping,
  // or a twice-weekly slot moved onto the other slot) is bad data, not a
  // scheduling choice — it stays blocked no matter what the therapist clicks.
  const hasSelfConflict = recurringConflicts.some((c) => c.selfConflict);
  const canOverrideConflicts = recurringConflicts.length > 0 && !hasSelfConflict;
  const canSubmitDespiteConflicts = () =>
    allowRecurringConflictsRef.current && !hasSelfConflict;

  const clearRecurringConflicts = () => {
    setRecurringConflicts([]);
    allowRecurringConflictsRef.current = false;
  };

  // Shared with the appointment wizard — see src/utils/appointmentFormRules.js.
  const validate = () => {
    const newErrors = {
      ...validatePatient(formData),
      ...validateSchedule(formData, { isEditing }),
      ...validateDetails(formData),
      ...validateExternalConflicts({
        hasBlockingConflicts:
          recurringConflicts.length > 0 && !canSubmitDespiteConflicts(),
        roomsEnabled,
        roomId: formData.roomId,
        roomConflicts,
        roomAvailabilityError,
      }),
    };
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Smoothly scroll the modal body to the first field flagged invalid, so the
  // user isn't left staring at the footer wondering why nothing happened.
  // Runs after the error state has re-rendered (rAF) so the markers exist in DOM.
  const scrollToFirstError = () => {
    requestAnimationFrame(() => {
      const container = modalContentRef.current;
      if (!container) return;
      const firstError = container.querySelector(
        ".error, .error-text, .apt-datetime-error"
      );
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      // Unresolved conflicts have no inline error marker — the panel below the
      // form body is the thing the therapist needs to see.
      conflictPanelRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  };

  // The conflict panel sits between the form body and the footer, so on a long
  // form it can be off-screen when the server rejects the series.
  const scrollToConflicts = () => {
    requestAnimationFrame(() => {
      conflictPanelRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  };

  // Submit gating: temp customer needs a name + valid Israeli mobile,
  // regular flow needs a selected patient.
  const hasValidPatient = formData.isTempCustomer
    ? formData.patientName.trim() !== "" &&
      formData.tempCustomerPhone?.startsWith("+972")
    : !!formData.patientId;

  // Get the patient's phone number
  const getPatientPhone = () => {
    // Temp customers have no patient record — phone comes from the form
    if (formData.isTempCustomer) {
      return formData.tempCustomerPhone?.startsWith("+972")
        ? formData.tempCustomerPhone
        : "";
    }
    const patient = effectivePatients.find((p) => p.id === formData.patientId);
    return patient?.phone || patient?.mobilePhone || "";
  };

  // Series confirmation SMS text — shared builder in appointmentSmsPreview.js.
  // `overrides` lets the late "send now" flow (edit mode) feed values from the
  // appointment doc / series occurrences instead of formData.
  const buildSeriesSmsMessage = (
    patientName,
    count,
    hebrewStartDate,
    hasLink = false,
    overrides = null
  ) =>
    buildSeriesSmsMessagePure({
      patientName,
      count,
      hebrewStartDate,
      hasLink,
      language: smsLanguage,
      frequency: overrides?.frequency ?? formData.recurringFrequency,
      baseDate: overrides?.baseDate ?? formData.date,
      baseTime: overrides?.baseTime ?? formData.startTime,
      secondDay: overrides ? overrides.secondDay ?? "" : formData.recurringSecondDay,
      secondTime: overrides ? overrides.secondTime ?? "" : formData.recurringSecondTime,
    });

  // Build SMS preview items for the preview screen — shared builder in
  // appointmentSmsPreview.js (also used by the appointment wizard).
  const buildSmsPreview = () =>
    buildSmsPreviewPure({
      formData,
      patientPhone: getPatientPhone(),
      clinicName: clinicProfile?.name || "",
      therapistSmsSettings,
      treatmentTypes,
      isPastDateTime,
      seriesItems: seriesPreview.items,
      language: smsLanguage,
    });

  // Opens the missing-rate gate and reports whether the caller must stop.
  // The typed-amount override is cleared here — opening the popup is the single
  // invalidation point, which keeps it out of the many places that write
  // `paymentAmount` and away from effects that would race `resumeAfterGate`.
  const openZeroAmountGateIfNeeded = () => {
    if (!needsAmountGate) {
      // The form carries a valid rate of its own now, so a value typed into the
      // popup earlier must not win over it — e.g. the therapist went back from
      // the SMS preview and edited the rate by hand.
      zeroAmountOverrideRef.current = null;
      return false;
    }
    if (zeroAmountConfirmedRef.current) return false;
    zeroAmountOverrideRef.current = null;
    setZeroAmountInput("");
    setZeroAmountError("");
    setShowZeroAmountConfirm(true);
    return true;
  };

  // Show preview before creating appointment
  const handleShowPreview = () => {
    gateOriginRef.current = "preview";
    if (!validate()) {
      scrollToFirstError();
      return;
    }
    if (isPastDateTime && !pastDateConfirmedRef.current && !isEditing) {
      setShowPastDateConfirm(true);
      return;
    }
    if (overlappingAppointment && !overlapConfirmedRef.current && !isEditing) {
      setShowOverlapConfirm(true);
      return;
    }
    if (roomsEnabled && accessibleRooms.length > 0 && !formData.roomId && !noRoomConfirmedRef.current && !isEditing) {
      setShowNoRoomConfirm(true);
      return;
    }
    if (openZeroAmountGateIfNeeded()) return;

    const items = buildSmsPreview();
    setSmsPreviewItems(items);
    setShowSmsDetails(false);
    setShowSmsPreview(true);
  };

  // Toggle an SMS item on/off. Turning a too-close reminder back on is an
  // explicit override (gapOverride); a past send time can never be overridden.
  const toggleSmsPreviewItem = (id) => {
    setSmsPreviewItems((prev) =>
      applySmsScheduleGuards(
        prev.map((item) => {
          if (item.id !== id) return item;
          const nextEnabled = !item.enabled;
          return {
            ...item,
            enabled: nextEnabled,
            gapOverride:
              nextEnabled && item.suppressedReason === SMS_SUPPRESS_REASON.TOO_CLOSE
                ? true
                : nextEnabled
                ? item.gapOverride
                : false,
          };
        })
      )
    );
  };

  // Master "send SMS" switch — enables/disables every message at once.
  // Suppressed messages stay off; the guards re-apply right after.
  const setAllSmsEnabled = (enabled) => {
    setSmsPreviewItems((prev) =>
      applySmsScheduleGuards(
        prev.map((item) => ({
          ...item,
          enabled,
          gapOverride: enabled ? item.gapOverride : false,
        }))
      )
    );
  };

  // Keep the create-form's `sendSms` opt-in in sync with the preview items so the
  // choice persists when the user goes back to the form or re-opens the preview.
  useEffect(() => {
    if (!showSmsPreview || smsPreviewItems.length === 0) return;
    const anyEnabled = smsPreviewItems.some((i) => i.enabled);
    setFormData((p) => (p.sendSms === anyEnabled ? p : { ...p, sendSms: anyEnabled }));
  }, [smsPreviewItems, showSmsPreview]);

  // Update scheduled send date/time of an SMS preview item (message stays fixed)
  const updateSmsPreviewItem = (id, field, value) => {
    setSmsPreviewItems((prev) =>
      applySmsScheduleGuards(
        prev.map((item) => {
          if (item.id !== id) return item;
          // A manual time pick re-opens the item: drop the previous suppression
          // and the gap override so the guards judge the new time on its own.
          // A suppressed item also re-enables — editing the send time is the
          // only way to fix a past send time, and leaving it off would hide
          // the editors again (they render only for enabled/suppressed items).
          const updated = {
            ...item,
            isImmediate: false,
            enabled: item.suppressedReason ? true : item.enabled,
            suppressedReason: null,
            gapOverride: false,
          };
          if (field === "date") {
            updated.scheduledDate = value;
            updated.scheduledDayName = HEBREW_DAYS_FULL[new Date(value).getDay()];
          } else if (field === "time") {
            updated.scheduledTime = value;
          }
          return updated;
        })
      )
    );
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    gateOriginRef.current = "submit";
    if (!validate()) {
      scrollToFirstError();
      return;
    }
    // Past date confirmation chain (only for new appointments)
    if (isPastDateTime && !pastDateConfirmedRef.current && !isEditing) {
      setShowPastDateConfirm(true);
      return;
    }
    if (overlappingAppointment && !overlapConfirmedRef.current && !isEditing) {
      setShowOverlapConfirm(true);
      return;
    }
    if (roomsEnabled && accessibleRooms.length > 0 && !formData.roomId && !noRoomConfirmedRef.current && !isEditing) {
      setShowNoRoomConfirm(true);
      return;
    }
    if (openZeroAmountGateIfNeeded()) return;

    const startDateTime = new Date(`${formData.date}T${formData.startTime}`);
    const endDateTime = new Date(startDateTime.getTime() + formData.duration * 60000);
    // A rate typed into the missing-rate popup wins: this call may have been
    // resumed through a closure that still holds the pre-popup `formData`.
    const paymentAmount =
      zeroAmountOverrideRef.current != null
        ? zeroAmountOverrideRef.current
        : formData.paymentAmount
          ? parseFloat(formData.paymentAmount)
          : null;

    // Extract SMS scheduling from preview items — shared with the wizard
    // (getSchedulingFromPreview in appointmentSmsPreview.js).
    const smsSchedulingData = getSchedulingFromPreview(
      smsPreviewItems,
      formData.isRecurring
    );

    const selectedTreatmentType = formData.treatmentTypeId
      ? treatmentTypes.find(t => t.id === formData.treatmentTypeId)
      : null;

    const appointmentData = {
      patientId: formData.isTempCustomer ? null : formData.patientId,
      patientName: formData.patientName,
      isTempCustomer: formData.isTempCustomer === true,
      tempCustomerPhone: formData.isTempCustomer
        ? formData.tempCustomerPhone
        : null,
      therapistName: therapistName,
      startTime: startDateTime.toISOString(),
      endTime: endDateTime.toISOString(),
      duration: parseInt(formData.duration, 10),
      notes: formData.notes,
      treatmentTypeId: formData.treatmentTypeId || null,
      treatmentTypeName: selectedTreatmentType?.name || null,
      paymentAmount,
      roomId: roomsEnabled && formData.roomId ? formData.roomId : null,
      // End-meeting SMS opt-in — only meaningful when the clinic feature is on
      // and the appointment is in the future (scheduled at completion time).
      sendEndMeetingSms:
        isEndMeetingSmsEnabled && !isPastDateTime && formData.sendEndMeetingSms === true,
      ...(isPastDateTime && !isEditing ? { isPastAppointment: true, status: "completed" } : {}),
      ...smsSchedulingData,
    };

    // Punch card args (top-level mutation args, never persisted on the doc).
    // Create: punch the new appointment(s) against the active card.
    // Edit (single appointments only): "punch" to deduct, "restore" to return
    // the visit to the card and revert to the regular payment flow.
    const wasPunched = isEditing && appointment?.isPunchCard === true;
    let punchArgs = {};
    if (punchCardsEnabled && showPunchOption) {
      if (!isEditing) {
        if (usePunchCard && activePunchCard) {
          punchArgs = {
            punchCard: {
              cardId: activePunchCard.id,
              patientId: formData.patientId,
            },
          };
        }
      } else if (usePunchCard && !wasPunched && activePunchCard) {
        punchArgs = {
          punchAction: "punch",
          punchCard: {
            cardId: activePunchCard.id,
            patientId: formData.patientId,
          },
        };
      } else if (!usePunchCard && wasPunched) {
        punchArgs = {
          punchAction: "restore",
          punchCard: {
            cardId: appointment.punchCardId,
            patientId: appointment.patientId,
          },
        };
      }
    }

    try {
      setSmsError(null);
      if (!isEditing) dispatch(showGlobalLoader("יוצר פגישה..."));

      if (isEditing) {
        if (appointment?.isRecurring && !pendingEditDataRef.current) {
          pendingEditDataRef.current = appointmentData;
          // The scope was already chosen and the therapist is now waiving a
          // conflict — don't ask the same question twice.
          if (!reuseEditScopeRef.current) {
            setShowRecurringEditDialog(true);
            return;
          }
        }

        if (appointment?.isRecurring && pendingEditDataRef.current) {
          const seriesResult = await updateRecurringAppointments({
            clinicId,
            appointmentId: appointment.id,
            updateData: pendingEditDataRef.current,
            scope: editScope,
            patientName: formData.patientName,
            therapistId,
            therapistName,
            allowConflicts: allowRecurringConflictsRef.current,
          }).unwrap();

          // Refused: the new day/time collides — either with the series' other
          // weekly slot (never waivable) or with an unrelated meeting of this
          // therapist (waivable). Nothing was written either way.
          if (seriesResult?.hasConflicts) {
            reuseEditScopeRef.current = false;
            // Surface the exact occurrences that collide instead of a bare
            // toast — same panel the create flow uses.
            if (seriesResult.waivable) {
              // Keep the pending data + scope so "עדכן בכל זאת" writes straight
              // through without re-asking about the series.
              allowRecurringConflictsRef.current = false;
              setRecurringConflicts(seriesResult.conflicts || []);
              showError("המועדים החדשים מתנגשים עם פגישות קיימות");
            } else {
              pendingEditDataRef.current = null;
              allowRecurringConflictsRef.current = false;
              setRecurringConflicts(seriesResult.conflicts || []);
              showError("המועד החדש מתנגש עם המועד השני בסדרה - יש לבחור יום או שעה אחרים");
            }
            scrollToConflicts();
            return;
          }
          pendingEditDataRef.current = null;
          reuseEditScopeRef.current = false;
          clearRecurringConflicts();
          const scopeLabel =
            editScope === "single"
              ? "הפגישה עודכנה בהצלחה"
              : editScope === "postpone_week"
              ? (seriesResult?.shiftedCount ?? 0) > 0
                ? `הפגישה עודכנה ו-${seriesResult.shiftedCount} המפגשים הבאים נדחו בשבוע`
                : "הפגישה עודכנה בהצלחה"
              : "הפגישות העתידיות עודכנו בהצלחה";
          showSuccess(scopeLabel);
        } else {
          await updateAppointment({
            clinicId,
            appointmentId: appointment.id,
            appointmentData,
            therapistId,
            therapistName,
            patientName: formData.patientName,
            ...punchArgs,
          }).unwrap();

          // Temp customer phone edited — confirmation links snapshot the phone
          // at creation, so sync them (reminder SMS + last-4 verification).
          if (
            formData.isTempCustomer &&
            formData.tempCustomerPhone?.startsWith("+972") &&
            formData.tempCustomerPhone !== appointment.tempCustomerPhone
          ) {
            try {
              await syncConfirmationLinkPhone(
                clinicId,
                appointment.id,
                formData.tempCustomerPhone
              );
            } catch (syncError) {
              console.error("[CK-3700] Error syncing temp customer link phone:", {
                errorCode: "CK-3700",
                errorMessage: syncError.message,
                appointmentId: appointment.id,
                timestamp: new Date().toISOString(),
              });
            }
          }
          showSuccess("הפגישה עודכנה בהצלחה");
        }
      } else if (formData.isRecurring) {
        const overridesMap = Object.keys(seriesOverrides).length > 0 ? seriesOverrides : null;
        const recurringConfig = {
          frequency: formData.recurringFrequency,
          totalCount: parseInt(formData.seriesTotalCount, 10),
          startIndex: parseInt(formData.seriesStartIndex, 10) || 1,
          overrides: overridesMap,
          allowConflicts: allowRecurringConflictsRef.current,
          secondSlot:
            formData.recurringFrequency === RECURRING_FREQUENCY.TWICE_WEEKLY &&
            formData.recurringSecondDay !== "" &&
            formData.recurringSecondTime
              ? {
                  dayOfWeek: Number(formData.recurringSecondDay),
                  time: formData.recurringSecondTime,
                }
              : null,
        };

        const result = await createRecurringAppointments({
          clinicId,
          therapistId,
          therapistName,
          appointmentData,
          recurringConfig,
          ...punchArgs,
        }).unwrap();

        if (result.punchedCount > 0 && result.punchedCount < (result.totalCreated || 0)) {
          showSuccess(
            `${result.punchedCount} פגישות נוקבו מהכרטיסיה, היתר נוצרו כפגישות רגילות`
          );
        }

        if (result.hasConflicts) {
          dispatch(hideGlobalLoader());
          const conflicts = result.conflicts || [];
          // Fresh conflict list ⇒ any earlier "create anyway" consent is stale.
          allowRecurringConflictsRef.current = false;
          setRecurringConflicts(conflicts);
          // The SMS preview is an overlay on top of the form — leaving it open
          // would hide the conflict panel and the modal would look frozen.
          // The preview items survive, so re-opening it restores the choices.
          setShowSmsPreview(false);
          const isBlocking = conflicts.some((c) => c.selfConflict);
          showError(
            isBlocking
              ? `נמצאו ${conflicts.length} התנגשויות בתוך הסדרה עצמה - יש לשנות את מועדי הסדרה`
              : `נמצאו ${conflicts.length} התנגשויות עם פגישות קיימות - ראה/י פירוט בתחתית החלון`
          );
          scrollToConflicts();
          return;
        }

        // Send SMS for recurring series
        const seriesInitialPreview = smsPreviewItems.find((i) => i.id === "series-initial");
        // Absent = confirmation disabled in settings → no series SMS.
        const shouldSendSeriesSms = seriesInitialPreview ? seriesInitialPreview.enabled : false;
        const isSeriesImmediate = seriesInitialPreview ? seriesInitialPreview.isImmediate !== false : true;
        if (shouldSendSeriesSms && isSeriesImmediate && !isOnboardingActive && result.appointments?.length > 0) {
          const patientPhone = getPatientPhone();
          if (patientPhone) {
            const canSendSms = await checkSMS(false);
            if (!canSendSms) {
              // Close the modal first, then show limit modal
              dispatch(hideGlobalLoader());
              onClose();
              setTimeout(() => checkSMS(true), 100); // Show modal after closing
              return;
            }

            setSmsSending(true);
            const recurringCancelHours = therapistSmsSettings?.cancelRestrictionEnabled
              ? therapistSmsSettings.cancelRestrictionHoursBefore
              : null;
            try {
              const firstAppt = result.appointments[0];
              const linkData = {
                language: smsLanguage,
                appointmentId: firstAppt.id,
                phoneNumber: patientPhone,
                patientName: formData.patientName,
                appointmentDate: formData.date,
                appointmentTime: formData.startTime,
                appointmentDuration: parseInt(formData.duration, 10),
                sentAutomatically: true,
                isRecurringSeries: true,
                recurringCount: result.totalCreated,
                cancelRestrictionHoursBefore: recurringCancelHours,
              };

              const initialLink = await createConfirmationLink({
                clinicId,
                linkData: { ...linkData, type: "initial" },
              }).unwrap();

              await sendAppointmentSMS({
                language: smsLanguage,
                name: formData.patientName,
                date: formData.date,
                time: formData.startTime,
                linkId: initialLink.id,
                phone: patientPhone,
                clinicName: clinicProfile?.name,
                customMessage: buildSeriesSmsMessage(
                  formData.patientName,
                  result.totalCreated,
                  formatHebrewDate(formData.date),
                  true
                ),
              });

              // Refresh usage cache after successful SMS send
              refreshUsageCache();

              await updateAppointment({
                clinicId,
                appointmentId: firstAppt.id,
                appointmentData: { whatsappInitialSent: true },
              }).unwrap();
            } catch (smsErr) {
              console.error("Error sending recurring series SMS:", smsErr);
            }
            setSmsSending(false);
          }
        }
      } else {
        const isPastAppt = isPastDateTime && pastDateConfirmedRef.current;
        const newAppointment = await createAppointment({
          clinicId,
          therapistId,
          therapistName,
          appointmentData,
          isAdminScheduling,
          scheduledByAdminId: isAdminScheduling ? scheduledByAdminId : null,
          scheduledByAdminName: isAdminScheduling ? scheduledByAdminName : null,
          ...punchArgs,
        }).unwrap();

        if (punchArgs.punchCard && newAppointment?.punchFallback === true) {
          showError(
            "לא נותרו ניקובים בכרטיסיה — הפגישה נוצרה כפגישה רגילה"
          );
        }

        // For non-past appointments: create confirmation links and send SMS
        const patientPhone = getPatientPhone();
        const cancelHours = therapistSmsSettings?.cancelRestrictionEnabled
          ? therapistSmsSettings.cancelRestrictionHoursBefore
          : null;
        const initialPreview = smsPreviewItems.find((i) => i.id === "single-initial");
        const reminderPreview = smsPreviewItems.find((i) => i.id === "single-reminder");
        // Absent preview item = message disabled in settings → not sent.
        const shouldSendInitial = initialPreview ? initialPreview.enabled : false;
        const shouldSendReminder = reminderPreview ? reminderPreview.enabled : false;
        const isInitialImmediate = initialPreview ? initialPreview.isImmediate !== false : true;
        const anySmsEnabled = shouldSendInitial || shouldSendReminder;

        if (!isPastAppt && patientPhone && newAppointment?.id && anySmsEnabled) {
           const linkData = {
             language: smsLanguage,
             appointmentId: newAppointment.id,
             phoneNumber: patientPhone,
             patientName: formData.patientName,
             appointmentDate: formData.date,
             appointmentTime: formData.startTime,
             appointmentDuration: parseInt(formData.duration, 10),
             sentAutomatically: shouldSendInitial && isInitialImmediate,
             cancelRestrictionHoursBefore: cancelHours,
           };

           // Only create the confirmation links for the messages that will
           // actually be used, so we don't leave orphan links behind.
           const [initialLink] = await Promise.all([
             shouldSendInitial
               ? createConfirmationLink({ clinicId, linkData: { ...linkData, type: "initial" } }).unwrap()
               : Promise.resolve(null),
             shouldSendReminder
               ? createConfirmationLink({ clinicId, linkData: { ...linkData, type: "reminder" } }).unwrap()
               : Promise.resolve(null),
           ]);

           // Only send immediately if initial SMS is enabled AND set to "now" (not scheduled for future)
           if (shouldSendInitial && isInitialImmediate && !isOnboardingActive && initialLink?.id) {
             const canSendSms = await checkSMS(false);
             if (!canSendSms) {
               dispatch(hideGlobalLoader());
               onClose();
               setTimeout(() => checkSMS(true), 100);
               return;
             }

             setSmsSending(true);
             const smsTreatmentTypeName = therapistSmsSettings?.includeTreatmentTypeInSms && formData.treatmentTypeId
               ? treatmentTypes.find(t => t.id === formData.treatmentTypeId)?.name || null
               : null;
             const smsResult = await sendAppointmentSMS({
               language: smsLanguage,
               name: formData.patientName,
               date: formData.date,
               time: formData.startTime,
               linkId: initialLink.id,
               phone: patientPhone,
               clinicName: clinicProfile?.name,
               treatmentTypeName: smsTreatmentTypeName,
             });

             if (!smsResult.success) {
               console.error("SMS sending failed:", smsResult.error);
               dispatch(hideGlobalLoader());
               setSmsError(smsResult.error);
               setSmsSending(false);
               return;
             }

             refreshUsageCache();

             await updateAppointment({
               clinicId,
               appointmentId: newAppointment.id,
               appointmentData: { whatsappInitialSent: true },
             }).unwrap();
             setSmsSending(false);
           }
        }
        
        if (isOnboardingActive && newAppointment?.id && newAppointment.patientId) {
          dispatch(hideGlobalLoader());
          onClose();
          setTimeout(() => navigate(`/dashboard/live-session/${newAppointment.id}?patientId=${newAppointment.patientId}`), 300);
          return;
        }
      }

      dispatch(hideGlobalLoader());
      if (!isEditing) {
        showSuccess(formData.isRecurring ? "סדרת המפגשים נוצרה בהצלחה" : "הפגישה נוצרה בהצלחה");
      }
      if (onSuccess && !isEditing) onSuccess();
      onClose();
    } catch (error) {
      dispatch(hideGlobalLoader());
      console.error("Error saving appointment:", error);
      setSmsError(error.message || "שגיאה בשמירת הפגישה");
      showError("שגיאה בשמירת הפגישה");
    }
  };

  const handleDeleteClick = () => {
    if (!appointment) return;
    if (appointment.isRecurring) {
      setRecurringDeleteState({
        linkedTransaction: undefined,
        preloadError: null,
        deleteLinkedTransactions: false,
        includePast: false,
        isWorking: false,
      });
      setShowRecurringDeleteDialog(true);
    } else {
      setDeleteDialogWorking(false);
      setShowDeleteConfirm(true);
    }
  };

  // Preload the linked transaction whenever the recurring-delete dialog opens,
  // so the dialog can show payment context for the current appointment. The
  // single-appointment dialog handles its own preload internally.
  useEffect(() => {
    if (!showRecurringDeleteDialog) return;
    if (!appointment?.id || !clinicId) return;
    if (recurringDeleteState.linkedTransaction !== undefined) return;
    let cancelled = false;
    (async () => {
      try {
        const tx = await getTransactionByAppointment(clinicId, appointment.id);
        if (cancelled) return;
        setRecurringDeleteState((prev) => ({
          ...prev,
          linkedTransaction: tx || null,
        }));
      } catch (err) {
        console.error("[CK-3417] Error preloading transaction for recurring delete:", {
          errorCode: "CK-3417",
          errorMessage: err.message,
          appointmentId: appointment.id,
          timestamp: new Date().toISOString(),
        });
        if (cancelled) return;
        setRecurringDeleteState((prev) => ({
          ...prev,
          linkedTransaction: prev.linkedTransaction ?? null,
          preloadError: err.message || "שגיאה בטעינת התשלום המקושר",
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showRecurringDeleteDialog, appointment?.id, clinicId, recurringDeleteState.linkedTransaction]);

  const handleDeleteConfirm = async (deleteLinkedTransaction) => {
    setDeleteDialogWorking(true);
    try {
      await deleteAppointment({
        clinicId,
        appointmentId: appointment.id,
        patientName: appointment.patientName,
        therapistId,
        therapistName,
        deleteLinkedTransaction: !!deleteLinkedTransaction,
      }).unwrap();
      showSuccess("הפגישה נמחקה בהצלחה");
      setShowDeleteConfirm(false);
      onClose();
    } catch (error) {
      console.error("Error deleting appointment:", error);
      showError("שגיאה במחיקת הפגישה");
    } finally {
      setDeleteDialogWorking(false);
    }
  };

  const handleRecurringDeleteConfirm = async () => {
    setRecurringDeleteState((prev) => ({ ...prev, isWorking: true }));
    try {
      const result = await deleteRecurringAppointments({
        clinicId,
        appointmentId: appointment.id,
        scope: deleteScope,
        patientName: appointment.patientName,
        therapistId,
        therapistName,
        currentTherapistId,
        isAdmin,
        includePast: !!recurringDeleteState.includePast,
        deleteLinkedTransactions:
          !!recurringDeleteState.linkedTransaction &&
          !!recurringDeleteState.deleteLinkedTransactions,
      }).unwrap();

      let scopeText;
      if (deleteScope === "single") {
        scopeText = "המפגש נמחק בהצלחה";
      } else if (result.deletedCount === 1) {
        scopeText = "מפגש אחד נמחק בהצלחה";
      } else {
        scopeText = `${result.deletedCount} מפגשים נמחקו בהצלחה`;
      }
      if (result.skippedCompletedCount > 0) {
        scopeText += ` (${result.skippedCompletedCount} מפגשים שכבר התקיימו לא נמחקו)`;
      }

      showSuccess(scopeText);
      setShowRecurringDeleteDialog(false);
      onClose();
    } catch (error) {
      console.error("Error deleting recurring appointments:", error);
      if (error.code === "permission-denied") {
        showError("אין לך הרשאה למחוק פגישות אלו. פנה למנהל הקליניקה.");
      } else {
        showError(error.message || "שגיאה במחיקת הפגישות");
      }
      setRecurringDeleteState((prev) => ({ ...prev, isWorking: false }));
    }
  };

  const handleRecurringEditConfirm = () => {
    // Ref guard closes the double-click window before the mutation's
    // isLoading flag kicks in — a double submit here used to create a second
    // set of financial transactions for the whole series.
    if (recurringConfirmInFlightRef.current) return;
    recurringConfirmInFlightRef.current = true;
    setShowRecurringEditDialog(false);
    const rowEdit = rowScopeEdit;
    setTimeout(async () => {
      try {
        if (rowEdit) {
          await persistOverviewRowUpdate(rowEdit.apt, { scope: editScope });
        } else {
          const syntheticEvent = { preventDefault: () => {} };
          await handleSubmit(syntheticEvent);
        }
      } finally {
        recurringConfirmInFlightRef.current = false;
      }
    }, 0);
  };

  const handleSeriesStatusChange = async (aptId, newStatus) => {
    // Cancelling a row goes through the shared cancel flow — same explanation
    // of what happens to the rest of the series, same "push it a week instead"
    // alternative, same linked-payment handling as every other entry point.
    if (newStatus === "cancelled") {
      const rowAppointment = seriesAppointments.find((apt) => apt.id === aptId);
      if (rowAppointment) {
        startCancel(rowAppointment);
        return;
      }
      // Row missing from the cached series (stale cache) — fall back to the
      // lightweight reason prompt rather than doing nothing.
      setSeriesCancelPrompt({
        open: true,
        aptId,
        reason: "",
        isWorking: false,
      });
      return;
    }
    try {
      await updateAppointment({
        clinicId,
        appointmentId: aptId,
        appointmentData: {
          status: newStatus,
          ...(newStatus === "completed" ? { completedBy: "user", completedAt: new Date().toISOString() } : {}),
        },
        therapistId,
        therapistName,
        patientName: formData.patientName,
      }).unwrap();
      showSuccess("המפגש סומן כהושלם");
      refetchSeries();
    } catch (error) {
      console.error("Error updating series appointment status:", error);
      showError("שגיאה בעדכון סטטוס המפגש");
    }
  };

  const handleSeriesCancelConfirm = async () => {
    const { aptId, reason } = seriesCancelPrompt;
    if (!aptId) return;
    setSeriesCancelPrompt((prev) => ({ ...prev, isWorking: true }));
    const trimmedReason = (reason || "").trim().slice(0, 300);
    try {
      await updateAppointment({
        clinicId,
        appointmentId: aptId,
        appointmentData: {
          status: "cancelled",
          cancelledAt: new Date().toISOString(),
          cancelledBy: "therapist",
          cancellationReason: trimmedReason || null,
          isApprovedByTherapist: true,
          isTherapistSummarizedTheMeeting: true,
          NotifyAboutThisMeetingApproval: false,
          NotifyAboutThisMeetingSummarize: false,
          NotifyAboutThisMeetingPaymentStatus: false,
        },
        therapistId,
        therapistName,
        patientName: formData.patientName,
      }).unwrap();
      showSuccess("המפגש סומן כבוטל");
      setSeriesCancelPrompt({
        open: false,
        aptId: null,
        reason: "",
        isWorking: false,
      });
      refetchSeries();
    } catch (error) {
      console.error("[CK-3416] Error cancelling series occurrence:", {
        errorCode: "CK-3416",
        errorMessage: error.message,
        appointmentId: aptId,
        timestamp: new Date().toISOString(),
      });
      showError(error?.message || "שגיאה בעדכון סטטוס המפגש");
      setSeriesCancelPrompt((prev) => ({ ...prev, isWorking: false }));
    }
  };

  const handleSeriesCancelDismiss = () => {
    if (seriesCancelPrompt.isWorking) return;
    setSeriesCancelPrompt({
      open: false,
      aptId: null,
      reason: "",
      isWorking: false,
    });
  };

  // True for occurrences that are in the past or already finalized
  // (completed/cancelled). We use this to (a) show a yellow warning banner
  // inside the inline-edit row and (b) require an explicit ConfirmDialog
  // before persisting changes — those updates can affect historical
  // documentation/reports, so we want admins to actively acknowledge.
  const isAppointmentPastOrFinalized = (apt) => {
    if (!apt) return false;
    if (apt.status === "completed" || apt.status === "cancelled") return true;
    if (!apt.startTime) return false;
    const startMs = new Date(apt.startTime).getTime();
    if (Number.isNaN(startMs)) return false;
    return startMs < Date.now();
  };

  const handleOverviewRowEdit = (apt) => {
    const aptDate = new Date(apt.startTime);
    const dateStr = aptDate.toLocaleDateString('en-CA');
    const timeStr = aptDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    setEditingOverviewRow(apt.id);
    setOverviewEditValues({
      date: dateStr,
      time: timeStr,
      treatmentTypeId: apt.treatmentTypeId || "",
      treatmentTypeName: apt.treatmentTypeName || "",
      therapistId: apt.therapistId || "",
      therapistName: apt.therapistName || "",
    });
  };

  // Builds the partial update payload from current `overviewEditValues` for
  // the given series-overview row. Returns `null` when nothing actually
  // changed so we can short-circuit without hitting Firestore.
  const buildOverviewRowUpdateData = (apt) => {
    const origDate = new Date(apt.startTime);
    const newStart = new Date(`${overviewEditValues.date}T${overviewEditValues.time}`);
    const durationMs = new Date(apt.endTime).getTime() - origDate.getTime();
    const newEnd = new Date(newStart.getTime() + durationMs);

    const updateData = {
      startTime: newStart.toISOString(),
      endTime: newEnd.toISOString(),
    };

    if (overviewEditValues.treatmentTypeId !== undefined) {
      updateData.treatmentTypeId = overviewEditValues.treatmentTypeId || null;
      updateData.treatmentTypeName = overviewEditValues.treatmentTypeName || null;
    }

    // Only persist a therapist change when (a) the field is exposed (admin +
    // multi-therapist patient) and (b) the value actually differs from the
    // current row. We deliberately do NOT cascade this change to
    // financialTransactions or treatmentDocs — those keep their original
    // therapist for audit/reporting purposes (per product decision).
    if (
      canReassignSeriesTherapist &&
      overviewEditValues.therapistId &&
      overviewEditValues.therapistId !== apt.therapistId
    ) {
      updateData.therapistId = overviewEditValues.therapistId;
      updateData.therapistName = overviewEditValues.therapistName || null;
    }

    return updateData;
  };

  // Rescheduling one row of the series is the same question the main form asks:
  // does the rest of the series follow, slide a week, or stay put? The row's
  // own date/time is the only thing that changed, so a therapist reassignment
  // or treatment-type tweak alone never opens the dialog.
  const persistOverviewRowUpdate = async (apt, { scope, allowConflicts = false } = {}) => {
    const updateData = buildOverviewRowUpdateData(apt);
    const rowMovedInTime =
      new Date(updateData.startTime).getTime() !== new Date(apt.startTime).getTime();

    if (apt.isRecurring && rowMovedInTime && !scope) {
      setRowScopeEdit({ apt, updateData });
      setShowRecurringEditDialog(true);
      return;
    }

    try {
      if (apt.isRecurring && scope && scope !== "single") {
        const seriesResult = await updateRecurringAppointments({
          clinicId,
          appointmentId: apt.id,
          updateData,
          scope,
          patientName: apt.patientName || formData.patientName,
          therapistId,
          therapistName,
          allowConflicts,
        }).unwrap();

        // Refused — nothing was written. Keep the row in edit mode so the
        // therapist can pick another slot or waive from the conflicts panel.
        if (seriesResult?.hasConflicts) {
          allowRecurringConflictsRef.current = false;
          setRecurringConflicts(seriesResult.conflicts || []);
          showError(
            seriesResult.waivable
              ? "המועדים החדשים מתנגשים עם פגישות קיימות"
              : "המועד החדש מתנגש עם המועד השני בסדרה - יש לבחור יום או שעה אחרים"
          );
          if (!seriesResult.waivable) setRowScopeEdit(null);
          scrollToConflicts();
          return;
        }

        clearRecurringConflicts();
        showSuccess(
          scope === "postpone_week"
            ? (seriesResult?.shiftedCount ?? 0) > 0
              ? `המפגש עודכן ו-${seriesResult.shiftedCount} המפגשים הבאים נדחו בשבוע`
              : "המפגש עודכן בהצלחה"
            : "המפגשים העתידיים עודכנו בהצלחה"
        );
      } else {
        await updateAppointment({
          clinicId,
          appointmentId: apt.id,
          appointmentData: updateData,
          // `therapistId` / `therapistName` here are forwarded to the activity
          // log (who performed the update), NOT the new owner of the
          // appointment. The new owner is encoded inside `updateData` above.
          therapistId,
          therapistName,
          patientName: apt.patientName || formData.patientName,
        }).unwrap();
        showSuccess("המפגש עודכן בהצלחה");
      }
      setRowScopeEdit(null);
      setEditingOverviewRow(null);
      setOverviewEditValues({});
      setPastReassignConfirm({ open: false, apt: null });
      refetchSeries();
    } catch (error) {
      console.error("[CK-7014b] Error updating series appointment:", {
        errorCode: "CK-7014b",
        errorMessage: error.message,
        appointmentId: apt.id,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בעדכון המפגש");
      setPastReassignConfirm({ open: false, apt: null });
    }
  };

  const handleOverviewRowSave = async (apt) => {
    // For past/finalized rows we surface a confirm dialog so the admin has to
    // explicitly acknowledge that they're rewriting historical data. The
    // dialog's onConfirm handler calls `persistOverviewRowUpdate` directly.
    if (isAppointmentPastOrFinalized(apt)) {
      setPastReassignConfirm({ open: true, apt });
      return;
    }
    await persistOverviewRowUpdate(apt);
  };

  // Resume whichever flow opened a gate popup. Preview must never fall through
  // to immediate creation — it always returns to the SMS-preview screen.
  const resumeAfterGate = () => {
    setTimeout(() => {
      if (gateOriginRef.current === "preview") {
        handleShowPreview();
      } else {
        handleSubmit({ preventDefault: () => {} });
      }
    }, 0);
  };

  // "Create anyway": the therapist has the full list of colliding meetings on
  // screen and accepts the double-booking. Re-submits the same series with the
  // waiver flag so the server skips the existing-appointment conflict check.
  const handleCreateDespiteConflicts = () => {
    allowRecurringConflictsRef.current = true;
    setErrors((prev) => {
      const { conflicts, ...rest } = prev;
      return rest;
    });

    // A refused series-overview row edit resumes on that row, not on the form.
    if (rowScopeEdit) {
      persistOverviewRowUpdate(rowScopeEdit.apt, {
        scope: editScope,
        allowConflicts: true,
      });
      return;
    }

    // Editing a series: the scope question was already answered before the
    // server refused, so go straight back to the write.
    if (isEditing && appointment?.isRecurring) {
      reuseEditScopeRef.current = true;
    }
    handleSubmit({ preventDefault: () => {} });
  };

  const handleOverlapConfirm = () => {
    overlapConfirmedRef.current = true;
    setOverlapConfirmed(true);
    setShowOverlapConfirm(false);
    resumeAfterGate();
  };

  // === Missing rate gate ===
  // Empty input = "create without a rate". A typed value is written to the form
  // (so the SMS preview and the rate field show it) *and* to the override ref,
  // which is what the resumed submit actually reads.
  const handleZeroAmountConfirm = () => {
    const raw = zeroAmountInput.trim();
    if (raw !== "") {
      const parsed = parseFloat(raw);
      if (Number.isNaN(parsed) || parsed <= 0) {
        setZeroAmountError("נא להזין תעריף גדול מ-0");
        return;
      }
      setFormData((prev) => ({ ...prev, paymentAmount: String(parsed) }));
      setRateSource("manual");
      zeroAmountOverrideRef.current = parsed;
    }
    zeroAmountConfirmedRef.current = true;
    setShowZeroAmountConfirm(false);
    resumeAfterGate();
  };

  // === Past Date Appointment Flow Handlers ===
  const handlePastDateConfirm = () => {
    pastDateConfirmedRef.current = true;
    setShowPastDateConfirm(false);
    resumeAfterGate();
  };

  const handleCreateLink = async (type) => {
    if (!appointment?.id || !clinicId) return;
    const startDate = new Date(appointment.startTime);
    const patientPhone = getPatientPhone();

    if (!patientPhone) {
      alert("לא נמצא מספר טלפון למטופל");
      return;
    }

    try {
      await createConfirmationLink({
        clinicId,
        linkData: {
          language: smsLanguage,
          appointmentId: appointment.id,
          type,
          phoneNumber: patientPhone,
          patientName: formData.patientName,
          appointmentDate: formatLocalDateString(startDate),
          appointmentTime: startDate.toTimeString().slice(0, 5),
          appointmentDuration: appointment.duration,
          sentAutomatically: false,
        },
      }).unwrap();
      refetchLinks();
    } catch (error) {
      console.error("Error creating confirmation link:", error);
    }
  };

  const copyLink = (linkId) => {
    const url = `${window.location.origin}/confirm/${linkId}`;
    navigator.clipboard.writeText(url);
    setCopiedLinkId(linkId);
    setTimeout(() => setCopiedLinkId(null), 2000);
  };
  
  // Selected patient details for UI
  const selectedPatientData = effectivePatients.find(p => p.id === formData.patientId);

  // Live SMS state for the open appointment (see `liveSmsState` above).
  useEffect(() => {
    if (!isEditing || !clinicId || !appointment?.id) {
      setLiveSmsState(null);
      return undefined;
    }
    setLiveSmsState(null);
    const unsubscribe = subscribeToAppointmentSmsState(
      clinicId,
      appointment.id,
      setLiveSmsState
    );
    return unsubscribe;
  }, [isEditing, clinicId, appointment?.id]);

  // The appointment as far as SMS scheduling is concerned: the cached doc with
  // the live fields layered on top once the listener has reported.
  const smsAppointment = useMemo(
    () => (liveSmsState ? { ...appointment, ...liveSmsState } : appointment),
    [appointment, liveSmsState]
  );

  // Helper to check confirmation status
  const getConfirmationStatus = () => {
    const initialLink = confirmationLinks.find(l => l.type === 'initial');
    // The day-before cron (CK-9410) sends real reminders through its own
    // "recurring_reminder" link and marks recurring1DayReminderSent — count
    // both so the modal doesn't claim "לא נשלח" for a reminder the patient got.
    const reminderLink =
      confirmationLinks.find(l => l.type === 'reminder') ||
      confirmationLinks.find(l => l.type === 'recurring_reminder');

    const initialSent = smsAppointment?.whatsappInitialSent === true;
    const reminderSent =
      smsAppointment?.whatsappReminderSent === true ||
      smsAppointment?.recurring1DayReminderSent === true;
    // A cancelled/completed/soft-deleted meeting has nothing left to send —
    // onAppointmentUpdated already deleted the queued Cloud Task (CK-9311),
    // but initialSmsScheduledAt/reminderSmsScheduledAt stay on the doc.
    // Treating those as "scheduled" would show a "מתוזמן" chip promising an
    // SMS that will never go out (with no trash icon to remove it).
    const smsStillSendable =
      !["cancelled", "completed"].includes(smsAppointment?.status) &&
      smsAppointment?.isDeleted !== true;
    // A send time that passed without the sent flag flipping means the queued
    // task was dropped or failed terminally (quota, dead link, legacy doc) —
    // keeping the "מתוזמן" chip up would promise an SMS that isn't coming.
    // 10-minute grace covers ASAP-clamped sends and Cloud Tasks retry lag.
    const notMissed = (scheduledAt) => {
      if (!scheduledAt) return false;
      const dt = new Date(scheduledAt);
      if (isNaN(dt.getTime())) return false;
      return dt.getTime() > Date.now() - 10 * 60 * 1000;
    };
    const initialScheduled = smsStillSendable && smsAppointment?.initialSmsEnabled !== false && notMissed(smsAppointment?.initialSmsScheduledAt) && !initialSent;
    const reminderScheduled = smsStillSendable && smsAppointment?.reminderSmsEnabled !== false && notMissed(smsAppointment?.reminderSmsScheduledAt) && !reminderSent;

    return {
      initial: {
        status: initialLink?.response || (smsAppointment?.whatsappInitialConfirmed ? 'confirmed' : smsAppointment?.whatsappInitialDeclined ? 'declined' : 'pending'),
        sent: initialSent,
        scheduled: initialScheduled,
        linkId: initialLink?.id
      },
      reminder: {
        status: reminderLink?.response || (smsAppointment?.whatsappReminderConfirmed ? 'confirmed' : smsAppointment?.whatsappReminderDeclined ? 'declined' : 'pending'),
        sent: reminderSent,
        scheduled: reminderScheduled,
        linkId: reminderLink?.id
      }
    };
  };

  // Human-readable reason a scheduled SMS terminally did not go out — written
  // on the appointment doc by the Cloud Task handlers (CK-9340/CK-9345).
  // Returns null for "sent" (the sent flags drive the UI) and for absent or
  // unknown outcomes, so legacy appointments render exactly as before.
  const smsOutcomeNotice = (outcome) => {
    switch (outcome) {
      case "skipped_quota":
        return "ההודעה לא נשלחה — מכסת ה-SMS החודשית נוצלה במלואה";
      case "skipped_declined":
        return "התזכורת לא נשלחה — המטופל/ת השיב/ה שלא יגיע/תגיע";
      case "skipped_no_phone":
        return "ההודעה לא נשלחה — לא נמצא מספר טלפון למטופל/ת";
      case "skipped_inactive":
        return "ההודעה לא נשלחה — המטופל/ת בארכיון";
      case "skipped_past":
        return "ההודעה לא נשלחה — מועד הפגישה חלף";
      case "skipped_opt_out":
        return "ההודעה לא נשלחה — המטופל/ת ביקש/ה להסיר אותו/ה מהודעות אוטומטיות";
      case "skipped_disabled_by_niv":
        return "ההודעה לא נשלחה — סוג הודעה זה כבוי כרגע במערכת";
      case "failed_no_link":
      case "failed_provider":
      case "failed_internal":
        return "שליחת ההודעה נכשלה — צוות קליניקיט קיבל התראה על כך";
      default:
        return null;
    }
  };

  const confirmationStatus = isEditing ? getConfirmationStatus() : null;
  const noSmsRequested = isEditing && (
    (smsAppointment?.initialSmsEnabled === false && smsAppointment?.reminderSmsEnabled === false) ||
    (confirmationStatus && !confirmationStatus.initial.sent && !confirmationStatus.initial.scheduled && !confirmationStatus.reminder.sent && !confirmationStatus.reminder.scheduled && confirmationLinks.length === 0)
  );

  // ============ Cancel a queued (not yet sent) SMS ============
  // Only a message that is still waiting in the Cloud Tasks queue can be
  // cancelled — a sent one is gone, and a cancelled/completed/past meeting has
  // nothing left to send. The write flips the appointment-level opt-out;
  // `onAppointmentUpdated` deletes the queued task server-side.
  const canCancelScheduledSms = (smsType) => {
    if (!isEditing || !confirmationStatus) return false;
    if (["cancelled", "completed"].includes(smsAppointment?.status)) return false;
    if (smsAppointment?.isDeleted === true) return false;
    const entry =
      smsType === "initial" ? confirmationStatus.initial : confirmationStatus.reminder;
    return entry.scheduled && !entry.sent;
  };

  const handleConfirmCancelScheduledSms = async () => {
    const smsType = cancelSmsTarget?.type;
    if (!smsType) return;
    setCancelSmsTarget(null);
    setCancellingSmsType(smsType);
    try {
      await cancelScheduledSms({
        clinicId,
        appointmentId: appointment.id,
        smsType,
        therapistId,
        therapistName,
        patientName: formData.patientName || appointment?.patientName,
      }).unwrap();
      showSuccess(
        smsType === "initial"
          ? "הודעת אישור ההגעה בוטלה ולא תישלח"
          : "הודעת התזכורת בוטלה ולא תישלח"
      );
    } catch (error) {
      console.error("[CK-3701] Error cancelling scheduled SMS:", {
        errorCode: "CK-3701",
        errorMessage: error?.message || error?.data?.message,
        appointmentId: appointment?.id,
        smsType,
        timestamp: new Date().toISOString(),
      });
      showError(
        error?.data?.message || error?.message || "ביטול ההודעה נכשל, נסה/י שוב"
      );
    } finally {
      setCancellingSmsType(null);
    }
  };

  // ============ Late "send confirmation SMS now" (edit mode) ============
  // Available only for appointments that were created without SMS and never
  // had one sent/scheduled. Sending puts the appointment in the same state as
  // a created-with-SMS one (yellow "ממתין" until the patient responds).
  const lateSmsStart = appointment?.startTime ? new Date(appointment.startTime) : null;
  const lateSmsPhone = isEditing ? getPatientPhone() : "";
  const canSendLateSms =
    isEditing &&
    noSmsRequested &&
    smsAppointment?.whatsappInitialSent !== true &&
    !smsAppointment?.initialSmsTaskName &&
    !["cancelled", "completed", "pending_approval"].includes(appointment?.status) &&
    appointment?.isDeleted !== true &&
    lateSmsStart &&
    lateSmsStart > new Date();

  // Future, still-active occurrences of the series (for the "whole series" scope)
  const lateSmsFutureOccurrences = useMemo(() => {
    if (!appointment?.recurringGroupId) return [];
    const now = new Date();
    return seriesAppointments
      .filter(
        (a) =>
          a.isDeleted !== true &&
          a.status !== "cancelled" &&
          a.status !== "completed" &&
          a.startTime &&
          new Date(a.startTime) > now
      )
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  }, [appointment?.recurringGroupId, seriesAppointments]);

  // Series scope hidden when some future occurrence already carries a sent
  // initial SMS (series message sent before — at create or from a sibling).
  const lateSmsSeriesAvailable =
    canSendLateSms &&
    appointment?.isRecurring &&
    lateSmsFutureOccurrences.length > 1 &&
    !lateSmsFutureOccurrences.some((a) => a.whatsappInitialSent === true);

  const lateSmsReminderHours = therapistSmsSettings?.reminderHoursBefore || 2;
  // Reminder offered only when enabled in the therapist's SMS settings AND its
  // send time hasn't passed (series scope: enough that some occurrence is far
  // enough out — per-occurrence times are checked at send).
  const lateSmsReminderOffered =
    therapistSmsSettings?.sendReminderEnabled !== false &&
    (lateSmsScope === "series" && lateSmsSeriesAvailable
      ? lateSmsFutureOccurrences.some(
          (a) => new Date(a.startTime).getTime() - lateSmsReminderHours * 3600000 > Date.now()
        )
      : lateSmsStart &&
        lateSmsStart.getTime() - lateSmsReminderHours * 3600000 > Date.now());

  // Local wall-clock ISO (no timezone) — the server parses it as Asia/Jerusalem
  // (parseSmsScheduledAt), matching the create-flow scheduling format.
  const toLocalIso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(
      d.getMinutes()
    ).padStart(2, "0")}:00`;

  const lateSmsTreatmentTypeName =
    therapistSmsSettings?.includeTreatmentTypeInSms === true && formData.treatmentTypeId
      ? treatmentTypes.find((t) => t.id === formData.treatmentTypeId)?.name || null
      : null;

  // Preview text shown in the dialog — same builders as the create-flow preview.
  const lateSmsPreviewText = useMemo(() => {
    if (!showLateSmsDialog || !lateSmsStart) return "";
    const patientName = formData.patientName || appointment?.patientName || "";
    if (lateSmsScope === "series" && lateSmsSeriesAvailable) {
      const anchor = lateSmsFutureOccurrences[0];
      const anchorStart = new Date(anchor.startTime);
      const anchorDateStr = formatLocalDateString(anchorStart);
      const anchorTimeStr = anchorStart.toTimeString().slice(0, 5);
      // Derive the second weekly slot (twice-weekly series) from the actual
      // future occurrences instead of formData, which is empty in edit mode.
      const anchorKey = `${anchorStart.getDay()}-${anchorTimeStr}`;
      const otherSlot = lateSmsFutureOccurrences
        .map((a) => new Date(a.startTime))
        .find((d) => `${d.getDay()}-${d.toTimeString().slice(0, 5)}` !== anchorKey);
      return buildSeriesSmsMessage(
        patientName,
        lateSmsFutureOccurrences.length,
        formatHebrewDate(anchorDateStr),
        false,
        {
          frequency: appointment?.recurringFrequency,
          baseDate: anchorDateStr,
          baseTime: anchorTimeStr,
          secondDay: otherSlot ? String(otherSlot.getDay()) : "",
          secondTime: otherSlot ? otherSlot.toTimeString().slice(0, 5) : "",
        }
      );
    }
    return buildConfirmationSmsText({
      type: "initial",
      patientName,
      clinicName: clinicProfile?.name || "",
      treatmentTypeName: lateSmsTreatmentTypeName || "",
      aptDate: formatLocalDateString(lateSmsStart),
      aptTime: lateSmsStart.toTimeString().slice(0, 5),
      language: smsLanguage,
    }).message;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLateSmsDialog, lateSmsScope, lateSmsSeriesAvailable, lateSmsFutureOccurrences, formData.patientName, formData.treatmentTypeId]);

  const handleLateSmsSend = async () => {
    if (!appointment?.id || !clinicId || !lateSmsPhone || lateSmsSending) return;
    setLateSmsSending(true);
    const createdLinkIds = [];
    try {
      // Quota pre-check — mirrors the create flow's limit-modal pattern.
      const canSend = await checkSMS(false);
      if (!canSend) {
        setLateSmsSending(false);
        setShowLateSmsDialog(false);
        onClose();
        setTimeout(() => checkSMS(true), 100);
        return;
      }

      const patientName = formData.patientName || appointment.patientName || "";
      const cancelHours = therapistSmsSettings?.cancelRestrictionEnabled
        ? therapistSmsSettings.cancelRestrictionHoursBefore
        : null;
      const isSeries = lateSmsScope === "series" && lateSmsSeriesAvailable;
      const includeReminder = lateSmsReminderOffered && lateSmsIncludeReminder;

      // The appointment whose initial link carries the confirmation: the
      // opened one for single scope, the first future occurrence for series.
      const anchor = isSeries ? lateSmsFutureOccurrences[0] : appointment;
      const anchorStart = new Date(anchor.startTime);
      const anchorDateStr = formatLocalDateString(anchorStart);
      const anchorTimeStr = anchorStart.toTimeString().slice(0, 5);

      const baseLinkData = {
        language: smsLanguage,
        appointmentId: anchor.id,
        phoneNumber: lateSmsPhone,
        patientName,
        appointmentDate: anchorDateStr,
        appointmentTime: anchorTimeStr,
        appointmentDuration: anchor.duration || appointment.duration,
        sentAutomatically: true,
        cancelRestrictionHoursBefore: cancelHours,
        ...(isSeries
          ? { isRecurringSeries: true, recurringCount: lateSmsFutureOccurrences.length }
          : {}),
      };

      // Reuse an unused active initial link (created earlier via copy-link)
      // when it belongs to the anchor — avoids orphan duplicates.
      let initialLinkId = null;
      const reusableInitial =
        anchor.id === appointment.id
          ? confirmationLinks.find((l) => l.type === "initial" && l.isActive !== false && !l.response)
          : null;
      if (reusableInitial && !isSeries) {
        initialLinkId = reusableInitial.id;
      } else {
        if (reusableInitial && isSeries) {
          // Stale single-appointment link — replace with a proper series link.
          await deactivateConfirmationLink({
            clinicId,
            linkId: reusableInitial.id,
            appointmentId: appointment.id,
          });
        }
        const created = await createConfirmationLink({
          clinicId,
          linkData: { ...baseLinkData, type: "initial" },
        }).unwrap();
        initialLinkId = created.id;
        createdLinkIds.push({ linkId: created.id, appointmentId: anchor.id });
      }

      // Single scope + reminder: the reminder task handler (CK-9340) requires
      // an active reminder link — create it up front so a send failure rolls
      // everything back together. Reuse an unused one if it exists.
      if (!isSeries && includeReminder) {
        const reusableReminder = confirmationLinks.find(
          (l) => l.type === "reminder" && l.isActive !== false && !l.response
        );
        if (!reusableReminder) {
          const createdReminder = await createConfirmationLink({
            clinicId,
            linkData: { ...baseLinkData, type: "reminder" },
          }).unwrap();
          createdLinkIds.push({ linkId: createdReminder.id, appointmentId: anchor.id });
        }
      }

      const smsResult = await sendAppointmentSMS({
        language: smsLanguage,
        name: patientName,
        date: anchorDateStr,
        time: anchorTimeStr,
        linkId: initialLinkId,
        phone: lateSmsPhone,
        clinicName: clinicProfile?.name,
        treatmentTypeName: isSeries ? null : lateSmsTreatmentTypeName,
        customMessage: isSeries ? lateSmsPreviewText : null,
      });

      if (!smsResult.success) {
        // Roll back links created here so the appointment stays cleanly "no SMS".
        for (const { linkId, appointmentId } of createdLinkIds) {
          await deactivateConfirmationLink({ clinicId, linkId, appointmentId });
        }
        console.error("[CK-3020] Late confirmation SMS send failed:", {
          errorCode: "CK-3020",
          errorMessage: smsResult.error,
          appointmentId: appointment.id,
          scope: lateSmsScope,
          timestamp: new Date().toISOString(),
        });
        showError("שליחת ה-SMS נכשלה, נסה/י שוב");
        setLateSmsSending(false);
        return;
      }

      refreshUsageCache();

      // Mark the anchor as sent — from here on the appointment behaves exactly
      // like one created with SMS (yellow card, CK-9313 reschedule flow, etc.).
      await updateAppointment({
        clinicId,
        appointmentId: anchor.id,
        appointmentData: {
          whatsappInitialSent: true,
          initialSmsEnabled: true,
          ...(!isSeries && includeReminder
            ? {
                reminderSmsEnabled: true,
                reminderSmsScheduledAt: toLocalIso(
                  new Date(anchorStart.getTime() - lateSmsReminderHours * 3600000)
                ),
              }
            : {}),
        },
        therapistId,
        therapistName,
        patientName,
      }).unwrap();

      // Series scope with reminders: enable a reminder per future occurrence
      // whose send time is still ahead. The CK-9316 server trigger schedules
      // the Cloud Task; the reminder link must exist for the task handler.
      let reminderFailures = 0;
      if (isSeries && includeReminder) {
        for (const occ of lateSmsFutureOccurrences) {
          const occStart = new Date(occ.startTime);
          const occReminderTime = new Date(
            occStart.getTime() - lateSmsReminderHours * 3600000
          );
          if (occReminderTime <= new Date()) continue;
          try {
            await createConfirmationLink({
              clinicId,
              linkData: {
                language: smsLanguage,
                appointmentId: occ.id,
                type: "reminder",
                phoneNumber: lateSmsPhone,
                patientName,
                appointmentDate: formatLocalDateString(occStart),
                appointmentTime: occStart.toTimeString().slice(0, 5),
                appointmentDuration: occ.duration || appointment.duration,
                sentAutomatically: true,
                cancelRestrictionHoursBefore: cancelHours,
              },
            }).unwrap();
            await updateAppointment({
              clinicId,
              appointmentId: occ.id,
              appointmentData: {
                reminderSmsEnabled: true,
                reminderSmsScheduledAt: toLocalIso(occReminderTime),
              },
              therapistId,
              therapistName,
              patientName,
            }).unwrap();
          } catch (occError) {
            reminderFailures += 1;
            console.error("[CK-3021] Late series reminder enable failed:", {
              errorCode: "CK-3021",
              errorMessage: occError.message,
              appointmentId: occ.id,
              timestamp: new Date().toISOString(),
            });
          }
        }
      }

      refetchLinks();
      if (isSeries) refetchSeries();
      if (reminderFailures > 0) {
        showError(`ההודעה נשלחה, אך תזמון התזכורת נכשל עבור ${reminderFailures} מפגשים`);
      } else {
        showSuccess(
          isSeries
            ? includeReminder
              ? "הודעת הסדרה נשלחה ותזכורות תוזמנו"
              : "הודעת הסדרה נשלחה למטופל/ת"
            : "הודעת ה-SMS נשלחה למטופל/ת"
        );
      }
      setShowLateSmsDialog(false);
    } catch (error) {
      console.error("[CK-3020] Late confirmation SMS flow failed:", {
        errorCode: "CK-3020",
        errorMessage: error.message,
        appointmentId: appointment?.id,
        scope: lateSmsScope,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשליחת ה-SMS");
    } finally {
      setLateSmsSending(false);
    }
  };

  // Israeli holiday / national day falling on the selected date (null on regular days)
  const dateHoliday = formData.date ? getHolidayForDateStr(formData.date) : null;

  // Shared scheduling cards (date/time/duration + treatment/rate/room).
  // Rendered in two places: directly under the patient card in EDIT mode,
  // and inside the active tab's content in CREATE mode. Single source so the
  // two layouts never drift.
  const schedulingCardsJsx = (
    <>
      {/* TIME CARD */}
      <div className="apt-card">
        <div className="apt-card-header">
          <div className="apt-card-icon green">
            <Calendar size={20} weight="fill" />
          </div>
          <span className="apt-card-title">
            {formData.isRecurring && !isEditing ? "תחילת סדרת המפגשים" : "מועד הפגישה"}
          </span>
        </div>

        <div className="apt-form-group">
          <label className="apt-label">
            {formData.isRecurring && !isEditing ? "תאריך התחלה" : "תאריך"}
          </label>
          <DatePicker
            name="date"
            value={formData.date}
            onChange={handleChange}
            disabled={isOnboardingActive}
            error={!!errors.date}
          />
          {dateHoliday && (
            <div className={`apt-holiday-warning ${dateHoliday.severity}`}>
              <Warning size={16} weight="fill" />
              <span>
                {dateHoliday.severity === "red"
                  ? `שימו לב: תאריך זה חל בחג — ${dateHoliday.name}`
                  : `שימו לב: תאריך זה חל ב${dateHoliday.name}`}
              </span>
            </div>
          )}
          {overlappingBlock && (
            <div className="apt-holiday-warning red">
              <Warning size={16} weight="fill" />
              <span>
                {`שימו לב: מועד הפגישה חופף לזמן חסום (${overlappingBlock.start}–${overlappingBlock.end})`}
              </span>
            </div>
          )}
        </div>

        <div className="time-row">
          <div className="time-col">
            <div className="apt-form-group">
              <label className="apt-label">
                {formData.isRecurring && !isEditing ? "שעה קבועה למפגשים בסדרה" : "שעה"}
              </label>
              <TimePicker
                name="startTime"
                value={formData.startTime}
                onChange={handleChange}
                disabled={isOnboardingActive}
                error={!!errors.startTime}
              />
            </div>
          </div>
          <div className="time-col">
            <div className="apt-form-group">
              <label className="apt-label">משך (דק׳)</label>
              <input
                type="number"
                name="duration"
                className="apt-input"
                value={formData.duration}
                onChange={handleChange}
                step="5"
              />
            </div>
          </div>
        </div>

        {/* Series scheduling hint — clarifies that these set the whole series */}
        {formData.isRecurring && !isEditing && (
          <div className="apt-series-time-note">
            <Info size={16} weight="fill" />
            <span>התאריך והשעה חלים על כל המפגשים בסדרה. ניתן לשנות מפגש מסוים ברשימה שלמטה.</span>
          </div>
        )}

        {/* Past date indicator (informational, not blocking) */}
        {isPastDateTime && !isEditing && (
          <div className="apt-past-date-notice">
            <CalendarX size={16} weight="fill" />
            <span>תאריך/שעה שעברו — הפגישה תיווצר רטרואקטיבית</span>
          </div>
        )}

        {/* Scheduling Errors */}
        {(errors.duration || errors.date || errors.startTime) && (
          <div className="apt-datetime-error">
            <Warning size={16} weight="fill" />
            <span>
              {[
                errors.duration,
                errors.date,
                errors.startTime
              ].filter(Boolean).join(' • ')}
            </span>
          </div>
        )}
      </div>

      {/* DETAILS CARD */}
      <div className="apt-card">
        <div className="apt-card-header">
          <div className="apt-card-icon green">
            <Stethoscope size={20} weight="fill" />
          </div>
          <span className="apt-card-title">פרטי טיפול</span>
        </div>

        <div className="apt-form-group">
          <label className="apt-label">סוג טיפול</label>
          <select
            name="treatmentTypeId"
            className="apt-select"
            value={formData.treatmentTypeId}
            onChange={handleChange}
            disabled={treatmentTypes.length === 0}
          >
            <option value="">{treatmentTypes.length === 0 ? "אין סוגי טיפול מוגדרים" : "בחר סוג טיפול..."}</option>
            {treatmentTypes.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          {treatmentTypes.length === 0 && (
            <div className="disabled-hint">
              <Info size={14} weight="bold" />
              <span>ניתן להגדיר סוגי טיפולים ב"הגדרות הקליניקה" (לשימוש פנימי בלבד)</span>
            </div>
          )}
        </div>

        {/* Multi-therapist: per-appointment therapist selection (single appointments only) */}
        {!isEditing && !formData.isRecurring && isMultiTherapistPatient && (
          <div className="apt-form-group">
            <label className="apt-label">מטפל/ת לפגישה</label>
            <select
              className="apt-select"
              value={appointmentTherapistId}
              onChange={(e) => setAppointmentTherapistId(e.target.value)}
            >
              {selectedPatientTherapistIds.map((tid) => {
                const t = clinicTherapists.find((ct) => ct.uid === tid);
                const name = t ? `${t.firstName} ${t.lastName}` : tid;
                return (
                  <option key={tid} value={tid}>
                    {name}{tid === currentTherapistId ? " (אני)" : ""}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        {showPunchOption && (
          <div className="apt-form-group apt-punch-card-group">
            <label className="apt-punch-card-checkbox">
              <input
                type="checkbox"
                checked={usePunchCard}
                onChange={(e) => handlePunchToggle(e.target.checked)}
              />
              <Ticket size={16} weight="duotone" />
              <span>מתוך כרטיסיה</span>
            </label>
            {activePunchCard && (
              <span className="apt-punch-card-hint">
                נותרו {remainingPunches} ניקובים בכרטיסיה · ₪
                {(activePunchCard.pricePerVisit || 0).toLocaleString("he-IL")}{" "}
                למפגש
                {activePunchCard.paidUpfront === true && " · שולם מראש"}
              </span>
            )}
            {usePunchCard &&
              !isEditing &&
              formData.isRecurring &&
              seriesPreview.count > remainingPunches && (
                <div className="apt-punch-series-notice">
                  <Info size={15} weight="fill" />
                  <span>
                    בכרטיסיה נותרו {remainingPunches} ניקובים בלבד.{" "}
                    {remainingPunches} הפגישות הראשונות ינוקבו מהכרטיסיה,
                    והיתר ({seriesPreview.count - remainingPunches}) ייווצרו
                    כפגישות רגילות בתעריף שבשדה. אם ברצונך לכסות את כל הסדרה,
                    ניתן קודם להוסיף מפגשים לכרטיסיה{" "}
                    <button
                      type="button"
                      className="apt-punch-card-link"
                      onClick={() =>
                        window.open(
                          `/dashboard/patients/${formData.patientId}?tab=punchCard`,
                          "_blank"
                        )
                      }
                    >
                      בפרופיל המטופל
                    </button>{" "}
                    וליצור את הסדרה לאחר מכן.
                  </span>
                </div>
              )}
          </div>
        )}

        {canAccessFinance && (
        <div className="apt-form-group">
          <label className="apt-label">תעריף (₪)</label>
          <div className="apt-amount-field">
            <input
              type="number"
              name="paymentAmount"
              className={`apt-input apt-amount-input${errors.paymentAmount ? " error" : ""}`}
              value={formData.paymentAmount}
              onChange={handleChange}
              onKeyDown={handlePaymentAmountKeyDown}
              placeholder="0.00"
              inputMode="decimal"
              disabled={rateDisabled}
            />
            <div className="apt-amount-stepper">
              <button
                type="button"
                className="apt-amount-step-btn"
                onClick={() => adjustPaymentAmount(1)}
                disabled={rateDisabled}
                tabIndex={-1}
                aria-label="הגדלת התעריף"
              >
                <CaretUp size={12} weight="bold" />
              </button>
              <button
                type="button"
                className="apt-amount-step-btn"
                onClick={() => adjustPaymentAmount(-1)}
                disabled={rateDisabled}
                tabIndex={-1}
                aria-label="הקטנת התעריף"
              >
                <CaretDown size={12} weight="bold" />
              </button>
            </div>
          </div>
          {errors.paymentAmount && (
            <span className="error-text">{errors.paymentAmount}</span>
          )}
          {rateSource && rateSource !== "manual" && formData.paymentAmount !== "" && (
            <span className="rate-source-hint">
              {rateSource === "patient" && "תעריף מטופל"}
              {rateSource === "treatmentType" && (() => {
                const tt = treatmentTypes.find(t => t.id === formData.treatmentTypeId);
                return tt ? `לפי: ${tt.name}` : "תעריף סוג טיפול";
              })()}
              {rateSource === "clinic" && "תעריף ברירת מחדל"}
              {rateSource === "punchCard" && "לפי כרטיסיה"}
            </span>
          )}
        </div>
        )}

        {roomsEnabled && accessibleRooms.length > 0 && (
          <div className="apt-form-group">
            <label className="apt-label">חדר</label>
            <select name="roomId" className="apt-select" value={formData.roomId} onChange={handleChange}>
              <option value="">ללא שיוך לחדר</option>
              {accessibleRooms.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            {roomAvailabilityError && <span className="error-text" style={{fontSize: '0.8rem', marginTop: 4, display: 'block'}}>{roomAvailabilityError}</span>}
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Plan limit modal for SMS limits */}
      <PlanLimitModalComponent />

      <div className={`modal-overlay ${isOnboardingActive ? 'onboarding-active' : ''}`} onClick={handleSafeClose}>
        <div className={`appointment-modal ${isOnboardingActive ? 'onboarding-active' : ''}`} onClick={(e) => e.stopPropagation()}>
        
        {/* HEADER */}
        <div className="apt-modal-header">
          <div className="apt-modal-title">
            <h2>{isEditing ? "עריכת פגישה" : "פגישה חדשה"}</h2>
            {isEditing && appointment?.isRecurring && (
              <span className="apt-modal-badge">
                <Repeat size={14} weight="bold" style={{marginLeft: 4}} />
                {appointment?.recurringIndex && appointment?.recurringTotalCount 
                  ? `מפגש ${appointment.recurringIndex} מתוך ${appointment.recurringTotalCount}`
                  : "סדרה"}
              </span>
            )}
          </div>
          <div className="apt-modal-header-actions">
            <HelpButton featureKey="appointment_modal" />
            <ModalCloseButton onClick={handleSafeClose} />
          </div>
        </div>

        {/* CONTENT */}
        <div className="apt-modal-content" ref={modalContentRef}>

          {/* Cancelled banner — shown instead of the delete/cancel footer buttons. */}
          {isEditing && liveAppointment?.status === "cancelled" && (
            <div className="apt-cancelled-banner">
              <div className="apt-cancelled-banner-main">
                <div className="apt-cancelled-banner-icon">
                  <CalendarX size={18} weight="fill" />
                </div>
                <div className="apt-cancelled-banner-text">
                  <strong>הפגישה בוטלה</strong>
                  {(() => {
                    const parts = [];
                    if (liveAppointment.cancelledBy === "patient") {
                      parts.push("המטופל ביטל את ההגעה דרך טופס האישור.");
                    } else if (liveAppointment.cancelledBy === "therapist") {
                      parts.push("הפגישה סומנה כבוטלה על ידי המטפל.");
                    }
                    if (liveAppointment.cancelledAt) {
                      try {
                        const when = new Date(
                          liveAppointment.cancelledAt.toDate
                            ? liveAppointment.cancelledAt.toDate()
                            : liveAppointment.cancelledAt
                        );
                        parts.push(
                          `בוטלה ב-${when.toLocaleDateString("he-IL", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}.`
                        );
                      } catch {
                        /* ignore malformed timestamp */
                      }
                    }
                    if (liveAppointment.cancellationFeeRecorded) {
                      parts.push("נרשמו דמי ביטול.");
                    }
                    return parts.length ? (
                      <span> {parts.join(" ")}</span>
                    ) : null;
                  })()}
                  {liveAppointment.cancellationReason && (
                    <div className="apt-cancelled-banner-reason">
                      <span className="apt-cancelled-banner-reason-label">
                        סיבה:
                      </span>{" "}
                      {liveAppointment.cancellationReason}
                    </div>
                  )}
                </div>
              </div>
              <div className="apt-cancelled-banner-actions">
                <button
                  type="button"
                  className="apt-cancelled-banner-restore-btn"
                  onClick={() => handleRestoreAppointment(liveAppointment)}
                  disabled={isRestoring}
                >
                  <ArrowsClockwise size={14} weight="bold" />
                  <span>
                    {isRestoring
                      ? "משחזר..."
                      : liveAppointment.isRecurring
                      ? "שחזר פגישה לסדרה"
                      : "שחזר פגישה"}
                  </span>
                </button>
              </div>
            </div>
          )}

          <div className="apt-grid-row">
            {/* PATIENT CARD */}
            <div className="apt-card full-width">
              <div className="apt-card-header">
                <div className="apt-card-icon green">
                  <User size={20} weight="fill" />
                </div>
                <span className="apt-card-title">פרטי המטופל</span>
              </div>
              
              {formData.patientId ? (
                <div className="patient-info-card">
                  <div className="patient-avatar">
                    {selectedPatientData?.firstName?.charAt(0)}
                  </div>
                  <div className="patient-details">
                    <span className="patient-name">{formData.patientName}</span>
                    <span className="patient-sub">
                      <Phone size={14} weight="fill" />
                      {selectedPatientData?.phone || selectedPatientData?.mobilePhone || "אין מספר טלפון"}
                    </span>
                  </div>
                  {!isEditing && (
                    <button className="change-patient-btn" onClick={() => handlePatientChange({target: {value: ""}})}>
                      החלף
                    </button>
                  )}
                </div>
              ) : formData.isTempCustomer ? (
                <div className="temp-customer-fields">
                  <div className="temp-customer-header">
                    <span className="temp-customer-badge">לקוח מזדמן</span>
                    {!isEditing && (
                      <button
                        className="change-patient-btn"
                        onClick={() => handlePatientChange({ target: { value: "" } })}
                      >
                        החלף
                      </button>
                    )}
                  </div>
                  <div className="apt-form-group">
                    <label className="apt-label">שם הלקוח</label>
                    <input
                      type="text"
                      className={`apt-input ${errors.tempCustomerName ? "error" : ""}`}
                      value={formData.patientName}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData((prev) => ({ ...prev, patientName: val }));
                        if (val.trim()) {
                          setErrors((prev) => {
                            const { tempCustomerName: _, ...rest } = prev;
                            return rest;
                          });
                        }
                      }}
                      placeholder="שם מלא"
                    />
                    {errors.tempCustomerName && (
                      <span className="error-text">{errors.tempCustomerName}</span>
                    )}
                  </div>
                  <div className="apt-form-group">
                    <label className="apt-label">מספר נייד</label>
                    <PhoneInput
                      value={formData.tempCustomerPhone}
                      onChange={(phone) => {
                        setFormData((prev) => ({
                          ...prev,
                          tempCustomerPhone: phone || "",
                        }));
                        if (phone?.startsWith("+972")) {
                          setErrors((prev) => {
                            const { tempCustomerPhone: _, ...rest } = prev;
                            return rest;
                          });
                        }
                      }}
                      error={errors.tempCustomerPhone}
                    />
                  </div>
                </div>
              ) : (
                <div className="apt-form-group">
                  <div className="apt-patient-select-row">
                    <select
                      className={`apt-select ${errors.patientId ? "error" : ""}`}
                      value={formData.patientId}
                      onChange={handlePatientChange}
                      disabled={isEditing}
                    >
                      <option value="">בחר מטופל מהרשימה...</option>
                      {effectivePatients.map(p => (
                        <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                      ))}
                      {tempCustomersEnabled && !isEditing && (
                        <option value={TEMP_CUSTOMER_OPTION}>
                          לקוח מזדמן (ללא תיק מטופל)
                        </option>
                      )}
                    </select>
                    <button
                      type="button"
                      className="apt-add-patient-btn"
                      data-tooltip="הוספת מטופל חדש"
                      onClick={() => {
                        onClose();
                        navigate("/dashboard/patients/new");
                      }}
                    >
                      <UserPlus size={18} weight="bold" />
                    </button>
                  </div>
                  {errors.patientId && <span className="error-text">{errors.patientId}</span>}
                </div>
              )}
            </div>

            {/* SCHEDULING CARDS (edit mode renders here; create mode renders them
                inside the active tab's content below). */}
            {isEditing && schedulingCardsJsx}

            {/* SERIES OVERVIEW CARD (edit mode) — full-width, same design as create mode */}
            {isEditing && appointment?.isRecurring && appointment?.recurringGroupId && (
              <div className="series-toggle-section full-width">
                <div 
                  className="series-toggle-row"
                  onClick={() => setShowSeriesOverview(!showSeriesOverview)}
                >
                  <div className="series-toggle-info">
                    <div className="series-toggle-icon">
                      <ListNumbers size={20} weight="bold" />
                    </div>
                    <div className="series-toggle-text">
                      <span className="series-toggle-title">סדרת מפגשים</span>
                      <span className="series-toggle-desc">
                        מפגש {appointment.recurringIndex || '—'} מתוך {appointment.recurringTotalCount || seriesAppointments.length || '—'}
                        {formData.recurringFrequency && ` • ${getFrequencyLabel(formData.recurringFrequency)}`}
                      </span>
                    </div>
                  </div>
                  <div className={`series-switch ck-apt-edit-series-overview-switch-r9x7 ${showSeriesOverview ? 'active' : ''}`}>
                    <div className="series-switch-knob" />
                  </div>
                </div>

                <div className={`series-options-panel ${showSeriesOverview ? 'open' : ''}`}>
                  <div className="series-options-inner">
                    {seriesAppointments.length > 0 && (
                      <div className="series-preview-table-wrap">
                        <div className="series-preview-header">
                          <ListNumbers size={16} weight="bold" />
                          <span>{seriesAppointments.length} מפגשים בסדרה</span>
                        </div>
                        <table className="series-preview-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>תאריך</th>
                              <th>יום</th>
                              <th>שעה</th>
                              {canReassignSeriesTherapist && <th>מטפל/ת</th>}
                              {treatmentTypes.length > 0 && <th>סוג טיפול</th>}
                              <th>סטטוס</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {seriesAppointments.map((apt) => {
                              const aptDate = new Date(apt.startTime);
                              const isCurrent = apt.id === appointment?.id;
                              const statusLabel = apt.status === 'completed' ? 'הושלם' : apt.status === 'cancelled' ? 'בוטל' : 'מתוכנן';
                              const statusClass = apt.status === 'completed' ? 'status-completed' : apt.status === 'cancelled' ? 'status-cancelled' : 'status-scheduled';
                              const canChangeStatus = apt.status === 'scheduled';
                              const isEditingRow = editingOverviewRow === apt.id;
                              // Allow opening the inline editor for rows that
                              // are scheduled (existing behavior) OR for admins
                              // in a multi-therapist clinic — including past /
                              // completed / cancelled rows — so they can fix a
                              // wrong therapist assignment after-the-fact.
                              const canEditRow = canChangeStatus || canReassignSeriesTherapist;
                              const rowIsPast = isAppointmentPastOrFinalized(apt);
                              // Resolve therapist display name with a fallback
                              // through `clinicTherapists` for legacy rows
                              // missing the denormalized `therapistName` field.
                              const aptTherapistName = (() => {
                                if (apt.therapistName) return apt.therapistName;
                                const t = clinicTherapists.find((ct) => ct.uid === apt.therapistId);
                                return t ? `${t.firstName} ${t.lastName}` : "—";
                              })();
                              // Restrict the reassignment dropdown to the
                              // therapists actually associated with this
                              // patient. Falls back to current therapistId if
                              // the patient list hasn't loaded yet.
                              const therapistOptions =
                                selectedPatientTherapistIds.length > 0
                                  ? selectedPatientTherapistIds
                                  : (apt.therapistId ? [apt.therapistId] : []);
                              return (
                                <Fragment key={apt.id}>
                                  {isEditingRow && rowIsPast && (
                                    <tr className="series-inline-warning-row">
                                      <td colSpan={4 + (canReassignSeriesTherapist ? 1 : 0) + (treatmentTypes.length > 0 ? 1 : 0) + 2}>
                                        <div className="series-inline-warning">
                                          <Warning size={14} weight="fill" />
                                          <span>שינוי שיוך לטיפול שעבר/הושלם ישפיע על תיעוד היסטורי ועל דוחות. ודא/י שזו אכן הפעולה הרצויה.</span>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                  <tr className={`${isCurrent ? 'series-current-row' : ''} ${isEditingRow ? 'series-overridden-row' : ''} ${!isEditingRow && rowIsPast ? 'series-row-past' : ''}`}>
                                  <td className="series-index-cell">{apt.recurringIndex || '—'} / {apt.recurringTotalCount || '—'}</td>
                                  {isEditingRow ? (
                                    <>
                                      <td>
                                        <DatePicker
                                          className="series-inline-datepicker"
                                          value={overviewEditValues.date || ''}
                                          onChange={(e) => setOverviewEditValues(prev => ({ ...prev, date: e.target.value }))}
                                        />
                                      </td>
                                      <td>{overviewEditValues.date ? HEBREW_DAYS_FULL[new Date(overviewEditValues.date + 'T00:00').getDay()] : '—'}</td>
                                      <td>
                                        <TimePicker
                                          className="series-inline-timepicker"
                                          value={overviewEditValues.time || ''}
                                          onChange={(e) => setOverviewEditValues(prev => ({ ...prev, time: e.target.value }))}
                                        />
                                      </td>
                                      {canReassignSeriesTherapist && (
                                        <td>
                                          <select
                                            className="series-inline-select"
                                            value={overviewEditValues.therapistId || apt.therapistId || ""}
                                            onChange={(e) => {
                                              const val = e.target.value;
                                              const t = clinicTherapists.find((ct) => ct.uid === val);
                                              setOverviewEditValues(prev => ({
                                                ...prev,
                                                therapistId: val,
                                                therapistName: t ? `${t.firstName} ${t.lastName}` : "",
                                              }));
                                            }}
                                          >
                                            {therapistOptions.map((tid) => {
                                              const t = clinicTherapists.find((ct) => ct.uid === tid);
                                              return (
                                                <option key={tid} value={tid}>
                                                  {t ? `${t.firstName} ${t.lastName}` : tid}
                                                </option>
                                              );
                                            })}
                                          </select>
                                        </td>
                                      )}
                                      {treatmentTypes.length > 0 && (
                                        <td>
                                          <select
                                            className="series-inline-select"
                                            value={overviewEditValues.treatmentTypeId || ""}
                                            onChange={(e) => {
                                              const val = e.target.value;
                                              const tt = treatmentTypes.find((t) => t.id === val);
                                              setOverviewEditValues(prev => ({
                                                ...prev,
                                                treatmentTypeId: val,
                                                treatmentTypeName: tt ? tt.name : "",
                                              }));
                                            }}
                                          >
                                            <option value="">—</option>
                                            {treatmentTypes.map((tt) => (
                                              <option key={tt.id} value={tt.id}>{tt.name}</option>
                                            ))}
                                          </select>
                                        </td>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <td>{formatHebrewDateUtil(aptDate)}</td>
                                      <td>{HEBREW_DAYS_FULL[aptDate.getDay()]}</td>
                                      <td>{aptDate.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}</td>
                                      {canReassignSeriesTherapist && (
                                        <td className="series-therapist-cell">{aptTherapistName}</td>
                                      )}
                                      {treatmentTypes.length > 0 && (
                                        <td className="series-type-cell">{apt.treatmentTypeName || "—"}</td>
                                      )}
                                    </>
                                  )}
                                  <td><span className={`series-status-tag ${statusClass}`}>{statusLabel}</span></td>
                                  <td className="series-actions-cell">
                                    <div className="series-action-buttons">
                                      {isEditingRow ? (
                                        <>
                                          <button
                                            type="button"
                                            className="series-action-btn complete"
                                            data-tooltip="שמור"
                                            onClick={() => handleOverviewRowSave(apt)}
                                          >
                                            <Check size={14} weight="bold" />
                                          </button>
                                          <button
                                            type="button"
                                            className="series-action-btn cancel"
                                            data-tooltip="ביטול"
                                            onClick={() => { setEditingOverviewRow(null); setOverviewEditValues({}); setRowScopeEdit(null); clearRecurringConflicts(); }}
                                          >
                                            <X size={14} weight="bold" />
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          {canEditRow && (
                                            <button
                                              type="button"
                                              className="series-action-btn edit"
                                              data-tooltip={rowIsPast ? "ערוך שיוך מטפל" : "ערוך מפגש"}
                                              onClick={() => handleOverviewRowEdit(apt)}
                                            >
                                              <NotePencil size={14} weight="bold" />
                                            </button>
                                          )}
                                          {canChangeStatus && (
                                            <button
                                              type="button"
                                              className="series-action-btn complete"
                                              data-tooltip="סמן כהושלם"
                                              onClick={() => handleSeriesStatusChange(apt.id, "completed")}
                                            >
                                              <CheckCircle size={14} weight="fill" />
                                            </button>
                                          )}
                                          {canChangeStatus && (
                                            <button
                                              type="button"
                                              className="series-action-btn cancel"
                                              data-tooltip="סמן כבוטל"
                                              onClick={() => handleSeriesStatusChange(apt.id, "cancelled")}
                                            >
                                              <CalendarX size={14} weight="fill" />
                                            </button>
                                          )}
                                          {apt.status === 'cancelled' && (
                                            <button
                                              type="button"
                                              className="series-action-btn restore"
                                              data-tooltip="שחזר מפגש"
                                              onClick={() => handleRestoreAppointment(apt)}
                                              disabled={restoringRowId === apt.id}
                                            >
                                              <ArrowsClockwise size={14} weight="bold" />
                                            </button>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </td>
                                  </tr>
                                </Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {seriesAppointments.length === 0 && (
                      <div className="series-info-badge">
                        <LoaderLoop size="xs" />
                        <span>טוען מפגשי סדרה...</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* CREATE MODE — tab switcher + tab content + shared notes/SMS card */}
            {!isEditing && (
              <>
                {/* TAB SWITCHER — single appointment vs. recurring series.
                    Sits directly under the patient card (top of the form). */}
                <div className="apt-series-tabs full-width" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={!formData.isRecurring}
                    className={`apt-series-tab ${!formData.isRecurring ? 'active' : ''}`}
                    onClick={() => setFormData(p => ({ ...p, isRecurring: false }))}
                  >
                    <Calendar size={18} weight="bold" />
                    <span>פגישה בודדת</span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={formData.isRecurring}
                    className={`apt-series-tab ${formData.isRecurring ? 'active' : ''} ${isOnboardingActive ? 'disabled' : ''}`}
                    disabled={isOnboardingActive}
                    onClick={() => !isOnboardingActive && setFormData(p => ({ ...p, isRecurring: true }))}
                  >
                    <ListNumbers size={18} weight="bold" />
                    <span>סדרת מפגשים</span>
                  </button>
                </div>

                {/* TAB CONTENT — scheduling cards are shared; series tab adds recurrence config */}
                <div className="apt-tab-content full-width">
                  <div className="apt-grid-row apt-tab-grid">
                    {schedulingCardsJsx}
                  </div>

                  {formData.isRecurring && (
                    <div className="series-config-panel">
                      <div className="series-options-inner">
                    <div className="series-inputs-row">
                      <div className="apt-form-group" style={{ flex: 1 }}>
                        <label className="apt-label">כמה מפגשים בסדרה?</label>
                        <input 
                          type="number" 
                          name="seriesTotalCount" 
                          className="apt-input" 
                          value={formData.seriesTotalCount} 
                          onChange={handleChange} 
                          min="2" 
                          max="100" 
                        />
                        {errors.seriesTotalCount && <span className="error-text">{errors.seriesTotalCount}</span>}
                      </div>
                      <div className="apt-form-group" style={{ flex: 1 }}>
                        <label className="apt-label">המפגש הבא בסדרה?</label>
                        <input 
                          type="number" 
                          name="seriesStartIndex" 
                          className="apt-input" 
                          value={formData.seriesStartIndex} 
                          onChange={handleChange} 
                          min="1" 
                          max={formData.seriesTotalCount || 100}
                        />
                        {errors.seriesStartIndex && <span className="error-text">{errors.seriesStartIndex}</span>}
                      </div>
                    </div>
                    <div className="apt-form-group">
                      <label className="apt-label">תדירות</label>
                      <select name="recurringFrequency" className="apt-select" value={formData.recurringFrequency} onChange={handleChange}>
                        <option value={RECURRING_FREQUENCY.TWICE_WEEKLY}>פעמיים בשבוע</option>
                        <option value={RECURRING_FREQUENCY.WEEKLY}>מפגש שבועי</option>
                        <option value={RECURRING_FREQUENCY.BIWEEKLY}>מפגש דו שבועי</option>
                        <option value={RECURRING_FREQUENCY.MONTHLY}>מפגש חודשי</option>
                      </select>
                    </div>

                    {formData.recurringFrequency === RECURRING_FREQUENCY.TWICE_WEEKLY && (
                      <div className="series-second-slot">
                        <label className="apt-label series-second-slot-title">המפגש השני בשבוע</label>
                        <div className="series-second-slot-row">
                          <div className="apt-form-group">
                            <label className="apt-label">יום בשבוע</label>
                            <select
                              className="apt-select"
                              name="recurringSecondDay"
                              value={formData.recurringSecondDay}
                              onChange={handleChange}
                            >
                              <option value="">בחר/י יום</option>
                              {WEEKDAY_OPTIONS.map((d) => (
                                <option key={d.value} value={d.value}>{d.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="apt-form-group">
                            <label className="apt-label">שעה</label>
                            <TimePicker
                              name="recurringSecondTime"
                              value={formData.recurringSecondTime}
                              onChange={handleChange}
                              error={!!errors.recurringSecondDay}
                            />
                          </div>
                        </div>
                        {errors.recurringSecondDay && <span className="error-text">{errors.recurringSecondDay}</span>}
                        <div className="series-second-slot-hint">
                          <Info size={14} weight="fill" />
                          <span>ניתן לערוך את התאריך והשעה של כל מפגש בנפרד ברשימת המפגשים שלמטה</span>
                        </div>
                      </div>
                    )}

                    {formData.recurringFrequency === RECURRING_FREQUENCY.TWICE_WEEKLY &&
                      seriesPreview.count === 0 &&
                      formData.date && (
                        <div className="series-second-slot-pending">
                          בחר/י יום ושעה למפגש השני כדי לראות את רשימת המפגשים
                        </div>
                      )}

                    {seriesPreview.count > 0 && (
                      <div className="series-preview-table-wrap">
                        <div className="series-preview-header">
                          <ListNumbers size={16} weight="bold" />
                          <span>
                            ייווצרו {seriesPreview.count} מפגשים (מתוך סדרה של {seriesPreview.totalCount})
                            {formData.recurringFrequency === RECURRING_FREQUENCY.TWICE_WEEKLY &&
                              ` • כ-${Math.ceil(seriesPreview.count / 2)} שבועות`}
                          </span>
                        </div>
                        <table className="series-preview-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>תאריך</th>
                              <th>יום</th>
                              <th>שעה</th>
                              {isMultiTherapistPatient && <th>מטפל/ת</th>}
                              {treatmentTypes.length > 0 && <th>סוג טיפול</th>}
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {seriesPreview.items.map((item, index) => {
                              const override = seriesOverrides[index];
                              const overrideTherapistId = override?.therapistId;
                              const overrideTreatmentTypeId = override?.treatmentTypeId;
                              const displayTherapistName = (() => {
                                const tid = overrideTherapistId || baseTherapistId;
                                const t = clinicTherapists.find((ct) => ct.uid === tid);
                                return t ? `${t.firstName} ${t.lastName}` : "";
                              })();
                              const displayTreatmentName = (() => {
                                const ttId = overrideTreatmentTypeId || formData.treatmentTypeId;
                                if (!ttId) return "—";
                                const tt = treatmentTypes.find((t) => t.id === ttId);
                                return tt ? tt.name : "—";
                              })();

                              return (
                              <tr key={index} className={item.isOverridden || overrideTherapistId || overrideTreatmentTypeId ? 'series-overridden-row' : ''}>
                                <td className="series-index-cell">{item.seriesIndex} / {seriesPreview.totalCount}</td>
                                {editingSeriesRow === index ? (
                                  <>
                                    <td>
                                      <DatePicker
                                        className="series-inline-datepicker"
                                        value={item.dateStr}
                                        onChange={(e) => setSeriesOverrides(prev => ({
                                          ...prev,
                                          [index]: { ...prev[index], date: e.target.value }
                                        }))}
                                      />
                                    </td>
                                    <td>
                                      {HEBREW_DAYS_FULL[item.date.getDay()]}
                                      {formData.recurringFrequency === RECURRING_FREQUENCY.TWICE_WEEKLY && (
                                        <span className={`series-slot-badge ${item.slot === SERIES_SLOT.SECONDARY ? 'secondary' : 'primary'}`}>
                                          {item.slot === SERIES_SLOT.SECONDARY ? 'מועד 2' : 'מועד 1'}
                                        </span>
                                      )}
                                    </td>
                                    <td>
                                      <TimePicker
                                        className="series-inline-timepicker"
                                        value={item.time}
                                        onChange={(e) => setSeriesOverrides(prev => ({
                                          ...prev,
                                          [index]: { ...prev[index], time: e.target.value }
                                        }))}
                                      />
                                    </td>
                                    {isMultiTherapistPatient && (
                                      <td>
                                        <select
                                          className="series-inline-select"
                                          value={override?.therapistId || baseTherapistId}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            const t = clinicTherapists.find((ct) => ct.uid === val);
                                            setSeriesOverrides(prev => ({
                                              ...prev,
                                              [index]: {
                                                ...prev[index],
                                                therapistId: val,
                                                therapistName: t ? `${t.firstName} ${t.lastName}` : "",
                                              }
                                            }));
                                          }}
                                        >
                                          {selectedPatientTherapistIds.map((tid) => {
                                            const t = clinicTherapists.find((ct) => ct.uid === tid);
                                            return (
                                              <option key={tid} value={tid}>
                                                {t ? `${t.firstName} ${t.lastName}` : tid}
                                              </option>
                                            );
                                          })}
                                        </select>
                                      </td>
                                    )}
                                    {treatmentTypes.length > 0 && (
                                      <td>
                                        <select
                                          className="series-inline-select"
                                          value={override?.treatmentTypeId || formData.treatmentTypeId || ""}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            const tt = treatmentTypes.find((t) => t.id === val);
                                            setSeriesOverrides(prev => ({
                                              ...prev,
                                              [index]: {
                                                ...prev[index],
                                                treatmentTypeId: val,
                                                treatmentTypeName: tt ? tt.name : null,
                                              }
                                            }));
                                          }}
                                        >
                                          <option value="">—</option>
                                          {treatmentTypes.map((tt) => (
                                            <option key={tt.id} value={tt.id}>{tt.name}</option>
                                          ))}
                                        </select>
                                      </td>
                                    )}
                                    <td className="series-actions-cell">
                                      <button
                                        type="button"
                                        className="series-action-btn complete"
                                        title="אישור"
                                        onClick={() => setEditingSeriesRow(null)}
                                      >
                                        <Check size={14} weight="bold" />
                                      </button>
                                    </td>
                                  </>
                                ) : (
                                  <>
                                    <td>{formatHebrewDateUtil(item.date)}</td>
                                    <td>
                                      {HEBREW_DAYS_FULL[item.date.getDay()]}
                                      {formData.recurringFrequency === RECURRING_FREQUENCY.TWICE_WEEKLY && (
                                        <span className={`series-slot-badge ${item.slot === SERIES_SLOT.SECONDARY ? 'secondary' : 'primary'}`}>
                                          {item.slot === SERIES_SLOT.SECONDARY ? 'מועד 2' : 'מועד 1'}
                                        </span>
                                      )}
                                    </td>
                                    <td>{item.time}</td>
                                    {isMultiTherapistPatient && <td className="series-therapist-cell">{displayTherapistName}</td>}
                                    {treatmentTypes.length > 0 && <td className="series-type-cell">{displayTreatmentName}</td>}
                                    <td className="series-actions-cell">
                                      <button
                                        type="button"
                                        className="series-edit-row-btn"
                                        title="ערוך מפגש"
                                        onClick={() => setEditingSeriesRow(index)}
                                      >
                                        <NotePencil size={14} weight="bold" />
                                      </button>
                                    </td>
                                  </>
                                )}
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Shared fields (apply to both tabs): internal notes + simple SMS
                    opt-in. Deep SMS config (text, send timing) lives in the preview screen. */}
                <div className="apt-card full-width">
                  <div className="apt-card-header">
                    <div className="apt-card-icon yellow">
                      <NotePencil size={20} weight="fill" />
                    </div>
                    <span className="apt-card-title">הערות ותקשורת</span>
                  </div>

                  <div
                    className={`checkbox-label ${(isOnboardingActive || isPastDateTime) ? 'onboarding-disabled' : ''}`}
                    onClick={() => !isOnboardingActive && !isPastDateTime && setFormData(p => ({...p, sendSms: !p.sendSms}))}
                    style={(isOnboardingActive || isPastDateTime) ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                  >
                     <div className={`recurring-toggle-box ${!isOnboardingActive && !isPastDateTime && formData.sendSms ? 'checked' : ''}`} style={{
                          width: 20, height: 20, border: '2px solid var(--color-primary)', borderRadius: 6,
                          background: !isOnboardingActive && !isPastDateTime && formData.sendSms ? 'var(--color-primary)' : 'transparent', marginRight: 8,
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                          {!isOnboardingActive && !isPastDateTime && formData.sendSms && <Check size={14} color="white" weight="bold" />}
                      </div>
                      <span>שלח הודעת SMS למטופל לאישור הגעה</span>
                      {isPastDateTime && (
                        <span className="sms-disabled-hint">(לא ניתן לשלוח SMS לפגישה שעברה)</span>
                      )}
                  </div>
                  {!isPastDateTime && !isOnboardingActive && formData.sendSms && (
                    <p className="sms-explanation-hint" style={{ marginTop: 8 }}>
                      את/ה תוכל/י לראות את נוסח ההודעה ולכוונן את מועדי השליחה במסך התצוגה המקדימה.
                    </p>
                  )}

                  {/* End-meeting (post-session) SMS opt-in — only when the
                      clinic feature is enabled and the appointment is in the future */}
                  {isEndMeetingSmsEnabled && (
                    <div
                      className={`checkbox-label ${(isOnboardingActive || isPastDateTime) ? 'onboarding-disabled' : ''}`}
                      onClick={() => !isOnboardingActive && !isPastDateTime && setFormData(p => ({...p, sendEndMeetingSms: !p.sendEndMeetingSms}))}
                      style={(isOnboardingActive || isPastDateTime) ? { opacity: 0.5, cursor: 'not-allowed', marginTop: 8 } : { marginTop: 8 }}
                    >
                      <div className={`recurring-toggle-box ${!isOnboardingActive && !isPastDateTime && formData.sendEndMeetingSms ? 'checked' : ''}`} style={{
                        width: 20, height: 20, border: '2px solid var(--color-primary)', borderRadius: 6,
                        background: !isOnboardingActive && !isPastDateTime && formData.sendEndMeetingSms ? 'var(--color-primary)' : 'transparent', marginRight: 8,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        {!isOnboardingActive && !isPastDateTime && formData.sendEndMeetingSms && <Check size={14} color="white" weight="bold" />}
                      </div>
                      <span>שלח הודעת סיום פגישה למטופל/ת</span>
                      {isPastDateTime && (
                        <span className="sms-disabled-hint">(לא ניתן לשלוח הודעת סיום לפגישה שעברה)</span>
                      )}
                    </div>
                  )}

                  <div className="apt-form-group" style={{ marginTop: 8 }}>
                    <textarea
                      name="notes"
                      className="apt-textarea"
                      placeholder="הערות לפגישה — הערות פנימיות שרק את/ה יכול/ה לראות"
                      value={formData.notes}
                      onChange={handleChange}
                    ></textarea>
                  </div>
                </div>
              </>
            )}

            {/* NOTES & SMS (edit mode only — confirmation status + internal notes).
                In create mode the notes + SMS opt-in live in a shared card below the
                tabs; the full SMS text/timing preview lives in the preview screen. */}
            {isEditing && (
            <div className="apt-card full-width">
              <div className="apt-card-header">
                <div className="apt-card-icon yellow">
                  <NotePencil size={20} weight="fill" />
                </div>
                <span className="apt-card-title">הערות ותקשורת</span>
              </div>

              {/* Confirmation Status (Editing Only) */}
              {isEditing && noSmsRequested && (
                <div className="confirmation-status-section">
                  <div className="confirmation-status-title">
                    <ChatCircleText size={16} weight="fill" />
                    <span>הודעות SMS</span>
                  </div>
                  <div className="no-sms-notice">
                    <Info size={16} weight="fill" />
                    <span>
                      {smsOutcomeNotice(smsAppointment?.reminderSmsOutcome) ||
                        smsOutcomeNotice(smsAppointment?.initialSmsOutcome) ||
                        "לא נשלחו הודעות SMS לאישור הגעה למטופל/ת"}
                    </span>
                  </div>
                  {canSendLateSms && (
                    <div className="late-sms-action">
                      <button
                        type="button"
                        className="btn btn-primary late-sms-send-btn"
                        disabled={!lateSmsPhone || lateSmsSending}
                        onClick={() => {
                          setLateSmsScope("single");
                          setLateSmsIncludeReminder(false);
                          setShowLateSmsDialog(true);
                        }}
                      >
                        <PaperPlaneTilt size={16} weight="fill" />
                        שלח SMS לאישור הגעה
                      </button>
                      {!lateSmsPhone && (
                        <span className="late-sms-no-phone">
                          לא ניתן לשלוח — למטופל/ת אין מספר טלפון בכרטיס
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
              {isEditing && confirmationStatus && !noSmsRequested && (
                <div className="confirmation-status-section">
                  <div className="confirmation-status-title">
                    <ChatCircleText size={16} weight="fill" />
                    <span>הודעות SMS</span>
                    <div className="sms-preview-info-tooltip">
                      <Question size={14} weight="bold" />
                      <div className="sms-preview-info-tooltip-content">
                        מועדי שליחת הודעות SMS נקבעים בעת יצירת הפגישה דרך מסך התצוגה המקדימה.
                      </div>
                    </div>
                  </div>
                  <div className="status-row">
                    <div className="status-item">
                      <div className="status-item-main">
                        <span className="status-label">אישור ראשוני:</span>
                        <span className={`apt-confirmation-tag ${confirmationStatus.initial.status}`}>
                          {confirmationStatus.initial.status === 'confirmed' && 'אושר'}
                          {confirmationStatus.initial.status === 'declined' && 'נדחה'}
                          {confirmationStatus.initial.status === 'pending' && (confirmationStatus.initial.sent ? 'נשלח' : confirmationStatus.initial.scheduled ? 'מתוזמן' : 'לא נשלח')}
                        </span>
                      </div>
                      {confirmationStatus.initial.scheduled && smsAppointment?.initialSmsScheduledAt && (
                        <span className="sms-scheduled-info">
                          <PaperPlaneTilt size={12} weight="fill" />
                          מתוזמן: {new Date(smsAppointment.initialSmsScheduledAt).toLocaleDateString("he-IL", { day: "numeric", month: "short" })} {new Date(smsAppointment.initialSmsScheduledAt).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit", hour12: false })}
                        </span>
                      )}
                      {!confirmationStatus.initial.sent &&
                        !confirmationStatus.initial.scheduled &&
                        smsOutcomeNotice(smsAppointment?.initialSmsOutcome) && (
                        <span className="sms-outcome-notice">
                          <Info size={12} weight="fill" />
                          {smsOutcomeNotice(smsAppointment?.initialSmsOutcome)}
                        </span>
                      )}
                      {(confirmationStatus.initial.linkId || canCancelScheduledSms("initial")) && (
                        <div className="sms-status-actions">
                          {confirmationStatus.initial.linkId && (
                            <button
                              type="button"
                              className="copy-link-btn"
                              onClick={() => copyLink(confirmationStatus.initial.linkId)}
                              title="העתק קישור"
                            >
                              {copiedLinkId === confirmationStatus.initial.linkId ? (
                                <Check size={14} weight="bold" />
                              ) : (
                                <Copy size={14} weight="bold" />
                              )}
                            </button>
                          )}
                          {canCancelScheduledSms("initial") && (
                            <button
                              type="button"
                              className="cancel-sms-btn"
                              disabled={cancellingSmsType === "initial"}
                              onClick={() => setCancelSmsTarget({ type: "initial" })}
                              title="בטל שליחת הודעה מתוזמנת"
                              aria-label="בטל שליחת הודעת אישור הגעה מתוזמנת"
                            >
                              {cancellingSmsType === "initial" ? (
                                <LoaderLoop size="xs" />
                              ) : (
                                <Trash size={14} weight="bold" />
                              )}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="status-item">
                      <div className="status-item-main">
                        <span className="status-label">תזכורת:</span>
                        <span className={`apt-confirmation-tag ${confirmationStatus.reminder.status}`}>
                          {confirmationStatus.reminder.status === 'confirmed' && 'אושר'}
                          {confirmationStatus.reminder.status === 'declined' && 'נדחה'}
                          {confirmationStatus.reminder.status === 'pending' && (confirmationStatus.reminder.sent ? 'נשלח' : confirmationStatus.reminder.scheduled ? 'מתוזמן' : 'לא נשלח')}
                        </span>
                      </div>
                      {confirmationStatus.reminder.scheduled && smsAppointment?.reminderSmsScheduledAt && (
                        <span className="sms-scheduled-info">
                          <PaperPlaneTilt size={12} weight="fill" />
                          מתוזמן: {new Date(smsAppointment.reminderSmsScheduledAt).toLocaleDateString("he-IL", { day: "numeric", month: "short" })} {new Date(smsAppointment.reminderSmsScheduledAt).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit", hour12: false })}
                        </span>
                      )}
                      {!confirmationStatus.reminder.sent &&
                        !confirmationStatus.reminder.scheduled &&
                        smsOutcomeNotice(smsAppointment?.reminderSmsOutcome) && (
                        <span className="sms-outcome-notice">
                          <Info size={12} weight="fill" />
                          {smsOutcomeNotice(smsAppointment?.reminderSmsOutcome)}
                        </span>
                      )}
                      {(confirmationStatus.reminder.linkId || canCancelScheduledSms("reminder")) && (
                        <div className="sms-status-actions">
                          {confirmationStatus.reminder.linkId && (
                            <button
                              type="button"
                              className="copy-link-btn"
                              onClick={() => copyLink(confirmationStatus.reminder.linkId)}
                              title="העתק קישור"
                            >
                              {copiedLinkId === confirmationStatus.reminder.linkId ? (
                                <Check size={14} weight="bold" />
                              ) : (
                                <Copy size={14} weight="bold" />
                              )}
                            </button>
                          )}
                          {canCancelScheduledSms("reminder") && (
                            <button
                              type="button"
                              className="cancel-sms-btn"
                              disabled={cancellingSmsType === "reminder"}
                              onClick={() => setCancelSmsTarget({ type: "reminder" })}
                              title="בטל שליחת הודעה מתוזמנת"
                              aria-label="בטל שליחת הודעת תזכורת מתוזמנת"
                            >
                              {cancellingSmsType === "reminder" ? (
                                <LoaderLoop size="xs" />
                              ) : (
                                <Trash size={14} weight="bold" />
                              )}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="apt-form-group" style={{ marginTop: 8 }}>
                <textarea
                  name="notes"
                  className="apt-textarea"
                  placeholder="הערות לפגישה — הערות פנימיות שרק את/ה יכול/ה לראות"
                  value={formData.notes}
                  onChange={handleChange}
                ></textarea>
              </div>
            </div>
            )}

          </div>
        </div>

        {/* Conflict panel — the server refused the series (create) or the
            series edit. Lists every colliding occurrence so the therapist can
            fix the dates, and offers an explicit double-booking override for
            collisions with OTHER appointments. Series-internal collisions
            (selfConflict) get no override — they'd double-book the patient
            against themselves. */}
        {recurringConflicts.length > 0 && (
          <div className="apt-conflicts-panel" ref={conflictPanelRef}>
            <div className="apt-conflicts-header">
              <Warning size={18} weight="fill" />
              <span>
                {hasSelfConflict
                  ? `נמצאו ${recurringConflicts.length} התנגשויות בתוך הסדרה עצמה`
                  : `נמצאו ${recurringConflicts.length} התנגשויות עם פגישות קיימות`}
              </span>
            </div>
            <ul className="apt-conflicts-list">
              {recurringConflicts.map((conflict, idx) => {
                const conflictDate = new Date(conflict.date);
                const existing = conflict.existingAppointment || {};
                const existingStart = existing.startTime
                  ? new Date(existing.startTime)
                  : null;
                const timeOf = (d) =>
                  d.toLocaleTimeString("he-IL", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  });
                return (
                  <li key={idx} className="apt-conflict-row">
                    <span className="apt-conflict-when">
                      {conflict.seriesIndex ? `מפגש ${conflict.seriesIndex} • ` : ""}
                      {formatHebrewDateUtil(conflictDate)} בשעה {timeOf(conflictDate)}
                    </span>
                    <span className="apt-conflict-with">
                      מתנגש עם {existing.patientName || "פגישה קיימת"}
                      {existingStart ? ` בשעה ${timeOf(existingStart)}` : ""}
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="apt-conflicts-actions">
              <span className="apt-conflicts-hint">
                {hasSelfConflict
                  ? "יש לשנות את מועדי הסדרה כדי להמשיך"
                  : isEditing
                  ? "ניתן לבחור מועד אחר, או לעדכן בכל זאת"
                  : "ניתן לערוך את מועדי המפגשים ברשימה שלמעלה, או ליצור את הסדרה בכל זאת"}
              </span>
              {canOverrideConflicts && (
                <button
                  type="button"
                  className="btn btn-warning apt-conflicts-force-btn"
                  onClick={handleCreateDespiteConflicts}
                  disabled={isCreating || isCreatingRecurring || isUpdatingRecurring}
                >
                  {isCreating || isCreatingRecurring || isUpdatingRecurring
                    ? isEditing
                      ? "מעדכן..."
                      : "יוצר..."
                    : isEditing
                    ? "עדכן בכל זאת"
                    : "צור בכל זאת"}
                </button>
              )}
            </div>
          </div>
        )}

        {/* FOOTER */}
        <div className="apt-modal-footer">
          <div className="footer-left">
            {(() => {
              // Footer button matrix for cancel/delete.
              //
              // - Delete (red, מחק): available for any non-cancelled,
              //   non-soft-deleted appointment — past OR future. The dialog
              //   itself adapts the copy and asks the therapist what to do
              //   with the linked payment (if any).
              // - Cancel (orange, בטל פגישה): for any editable appointment
              //   that isn't already cancelled/deleted (past or future).
              //   Opens the shared `useCancelAppointmentFlow` modal.
              // - Cancelled / soft-deleted: no buttons.
              if (!isEditing || !appointment) return null;
              // Read from the subscribed doc — after a restore the snapshot
              // prop still says "cancelled" and the buttons must come back.
              const isAlreadyCancelled = liveAppointment?.status === "cancelled";
              const isSoftDeleted = liveAppointment?.isDeleted === true;
              if (isAlreadyCancelled || isSoftDeleted) return null;
              return (
                <>
                  <button
                    className="btn btn-danger"
                    type="button"
                    onClick={handleDeleteClick}
                  >
                    <Trash size={18} weight="bold" />
                    <span>מחק</span>
                  </button>
                  <button
                    className="btn btn-warning"
                    type="button"
                    onClick={() => startCancel(appointment)}
                  >
                    <X size={18} weight="bold" />
                    <span>בטל פגישה</span>
                  </button>
                </>
              );
            })()}
          </div>
          <div className="footer-right">
             <CustomClinikitActionButton
               variant="secondary"
               type="button"
               onClick={handleSafeClose}
             >
               ביטול
             </CustomClinikitActionButton>
             <div className={(isEditing && !isFormDirty) || (!isEditing && !hasValidPatient) ? "btn-primary-wrap" : ""} data-tooltip-disabled={isEditing && !isFormDirty ? "לא בוצעו שינויים בפגישה" : !isEditing && !hasValidPatient ? (formData.isTempCustomer ? "נא להזין שם ומספר נייד" : "נא לבחור מטופל") : undefined}>
               {isEditing ? (
                 <button className="btn btn-primary" type="button" onClick={handleSubmit} disabled={isCreating || isUpdating || !isFormDirty}>
                   {isCreating || isUpdating ? "שומר..." : "עדכן פגישה"}
                 </button>
               ) : (
                 <CustomClinikitActionButton
                   type="button"
                   icon={<Eye size={16} weight="bold" />}
                   onClick={handleShowPreview}
                   disabled={isCreating || isUpdating || !hasValidPatient}
                 >
                   תצוגה מקדימה
                 </CustomClinikitActionButton>
               )}
             </div>
          </div>
        </div>

      </div>

      {/* Single-appointment delete dialog (shared component used by both
          the appointment modal and the calendar context menu). */}
      <DeleteAppointmentDialog
        isOpen={showDeleteConfirm && !!appointment}
        appointment={appointment}
        clinicId={clinicId}
        isWorking={deleteDialogWorking || isDeleting}
        onClose={() => {
          if (deleteDialogWorking) return;
          setShowDeleteConfirm(false);
        }}
        onConfirm={({ deleteLinkedTransaction }) =>
          handleDeleteConfirm(deleteLinkedTransaction)
        }
      />

      <ConfirmDialog
        isOpen={showOverlapConfirm}
        onClose={() => setShowOverlapConfirm(false)}
        onConfirm={handleOverlapConfirm}
        title="התנגשות פגישות"
        message={`קיימת כבר פגישה לתאריך ושעה אלה${
          overlappingAppointment
            ? ` (${overlappingAppointment.patientName} ב-${new Date(
                overlappingAppointment.startTime
              ).toLocaleTimeString("he-IL", {
                hour: "2-digit",
                minute: "2-digit",
                })})`
            : ""
        }. האם אתה בטוח שברצונך לתאם פגישה נוספת בטווח הזמן הזה?`}
        type="warning"
        confirmText={gateOriginRef.current === "preview" ? "כן, עבור לתצוגה מקדימה" : "כן, צור פגישה נוספת"}
        cancelText="ביטול"
      />

      <ConfirmDialog
        isOpen={!!cancelSmsTarget}
        onClose={() => setCancelSmsTarget(null)}
        onConfirm={handleConfirmCancelScheduledSms}
        title="ביטול הודעת SMS מתוזמנת"
        message={
          cancelSmsTarget?.type === "reminder"
            ? `הודעת התזכורת שממתינה בתור${
                smsAppointment?.reminderSmsScheduledAt
                  ? ` (${new Date(smsAppointment.reminderSmsScheduledAt).toLocaleDateString("he-IL", { day: "numeric", month: "short" })} בשעה ${new Date(smsAppointment.reminderSmsScheduledAt).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit", hour12: false })})`
                  : ""
              } תבוטל ולא תישלח למטופל/ת. לא ניתן לשחזר את התזמון — יהיה צורך לתזמן מחדש. להמשיך?`
            : `הודעת אישור ההגעה שממתינה בתור${
                smsAppointment?.initialSmsScheduledAt
                  ? ` (${new Date(smsAppointment.initialSmsScheduledAt).toLocaleDateString("he-IL", { day: "numeric", month: "short" })} בשעה ${new Date(smsAppointment.initialSmsScheduledAt).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit", hour12: false })})`
                  : ""
              } תבוטל ולא תישלח למטופל/ת. לא ניתן לשחזר את התזמון — יהיה צורך לתזמן מחדש. להמשיך?`
        }
        confirmText="כן, בטל את ההודעה"
        cancelText="השאר מתוזמן"
        type="danger"
        icon="delete"
      />

      <ConfirmDialog
        isOpen={showCloseWarning}
        onClose={() => setShowCloseWarning(false)}
        onConfirm={handleConfirmClose}
        title="יש שינויים שלא נשמרו"
        message={
          isEditing
            ? "יש שינויים שלא נשמרו בפגישה. האם אתה בטוח שברצונך לסגור בלי לשמור?"
            : "יש נתונים שהוזנו בטופס. האם אתה בטוח שברצונך לסגור בלי ליצור פגישה?"
        }
        confirmText="סגור ללא שמירה"
        cancelText="המשך עריכה"
        type="warning"
      />

      <ConfirmDialog
        isOpen={showNoRoomConfirm}
        onClose={() => setShowNoRoomConfirm(false)}
        onConfirm={() => {
          noRoomConfirmedRef.current = true;
          setNoRoomConfirmed(true);
          setShowNoRoomConfirm(false);
          resumeAfterGate();
        }}
        title="פגישה ללא שיוך לחדר"
        message="האם אתה בטוח שברצונך לקבוע פגישה בלי לשייך אותה לחדר טיפול?"
        confirmText={gateOriginRef.current === "preview" ? "כן, המשך לתצוגה מקדימה" : "כן, קבע פגישה"}
        cancelText="בחר חדר"
        type="warning"
      />

      {/* Missing rate: a meeting without an amount never creates a financial
          transaction, so it disappears from collection, expected income and
          reports. The therapist can fix it right here or knowingly skip it. */}
      {(() => {
        const typed = zeroAmountInput.trim();
        const typedValid = typed !== "" && parseFloat(typed) > 0;
        const typedInvalid = typed !== "" && !typedValid;
        const toPreview = gateOriginRef.current === "preview";
        const seriesCount = seriesPreview?.count || 0;
        return (
          <ConfirmDialog
            isOpen={showZeroAmountConfirm}
            onClose={() => setShowZeroAmountConfirm(false)}
            onConfirm={handleZeroAmountConfirm}
            title="לא הוזן תעריף לפגישה"
            message={
              formData.isRecurring
                ? `חשוב שלכל מפגש יהיה תעריף. ללא תעריף${
                    seriesCount > 1 ? ` ${seriesCount} המפגשים בסדרה` : " המפגשים בסדרה"
                  } לא ייכללו במסכי הגבייה, בהכנסות הצפויות ובדוחות.`
                : "חשוב שלכל פגישה יהיה תעריף. ללא תעריף הפגישה לא תופיע במסכי הגבייה, לא תיכלל בהכנסות הצפויות ולא תיספר בדוחות."
            }
            type="warning"
            icon="warning"
            confirmDisabled={typedInvalid}
            confirmText={
              typedValid
                ? toPreview
                  ? "המשך עם התעריף"
                  : "שמור עם התעריף"
                : toPreview
                  ? "כן, המשך ללא תעריף"
                  : "כן, צור ללא תעריף"
            }
            cancelText="חזרה לעריכה"
          >
            <label className="apt-zero-amount-label" htmlFor="apt-zero-amount">
              הזנת תעריף (₪)
            </label>
            <input
              id="apt-zero-amount"
              type="number"
              className={`apt-zero-amount-input${typedInvalid ? " error" : ""}`}
              value={zeroAmountInput}
              onChange={(e) => {
                setZeroAmountInput(e.target.value);
                setZeroAmountError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleZeroAmountConfirm();
                }
              }}
              placeholder="0.00"
              inputMode="decimal"
              autoFocus
            />
            {zeroAmountError && (
              <span className="apt-zero-amount-error">{zeroAmountError}</span>
            )}
          </ConfirmDialog>
        );
      })()}

      {/* Past Date Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showPastDateConfirm}
        onClose={() => setShowPastDateConfirm(false)}
        onConfirm={handlePastDateConfirm}
        title="פגישה על תאריך שעבר"
        message="האם אתה בטוח שברצונך ליצור פגישה על תאריך ושעה שכבר עברו?"
        confirmText="כן, צור פגישה"
        cancelText="ביטול"
        type="warning"
      />

      {/* Series-overview: confirm changes to past/finalized appointments.
          Triggered from `handleOverviewRowSave` when the row is past or
          completed/cancelled. The actual update runs in `persistOverviewRowUpdate`. */}
      <ConfirmDialog
        isOpen={pastReassignConfirm.open}
        onClose={() => setPastReassignConfirm({ open: false, apt: null })}
        onConfirm={() => {
          if (pastReassignConfirm.apt) {
            persistOverviewRowUpdate(pastReassignConfirm.apt);
          }
        }}
        title="עדכון טיפול שעבר"
        message="הטיפול שבחרת לערוך כבר התקיים (או סומן כהושלם/בוטל). שינוי השיוך או הפרטים ישפיע על תיעוד היסטורי ועל דוחות. האם להמשיך?"
        confirmText="כן, עדכן"
        cancelText="ביטול"
        type="warning"
      />

      {/* Late "send confirmation SMS now" dialog */}
      {showLateSmsDialog && (
        <div
          className="modal-overlay recurring-delete-overlay"
          onClick={() => {
            if (!lateSmsSending) setShowLateSmsDialog(false);
          }}
        >
          <div
            className="recurring-delete-dialog late-sms-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dialog-header">
              <PaperPlaneTilt size={24} weight="bold" className="dialog-icon" />
              <h3>שליחת SMS לאישור הגעה</h3>
            </div>

            {lateSmsSeriesAvailable && (
              <>
                <p className="dialog-message">
                  פגישה זו היא חלק מסדרת מפגשים. עבור מה לשלוח את ההודעה?
                </p>
                <div className="delete-scope-options">
                  <label className="scope-option">
                    <input
                      type="radio"
                      name="lateSmsScope"
                      value="single"
                      checked={lateSmsScope === "single"}
                      onChange={(e) => setLateSmsScope(e.target.value)}
                      disabled={lateSmsSending}
                    />
                    <span className="scope-label">פגישה זו בלבד</span>
                  </label>
                  <label className="scope-option">
                    <input
                      type="radio"
                      name="lateSmsScope"
                      value="series"
                      checked={lateSmsScope === "series"}
                      onChange={(e) => setLateSmsScope(e.target.value)}
                      disabled={lateSmsSending}
                    />
                    <span className="scope-label">
                      כל הסדרה ({lateSmsFutureOccurrences.length} מפגשים עתידיים)
                    </span>
                  </label>
                </div>
              </>
            )}

            <div className="late-sms-preview">
              <div className="late-sms-preview-title">
                <ChatCircleText size={15} weight="fill" />
                <span>תוכן ההודעה</span>
              </div>
              <div className="sms-preview-bubble">
                <p>{lateSmsPreviewText}</p>
                <span className="sms-preview-link-placeholder">
                  🔗 קישור לאישור הגעה
                </span>
              </div>
            </div>

            {lateSmsReminderOffered && (
              <label className="scope-option late-sms-reminder-option">
                <input
                  type="checkbox"
                  checked={lateSmsIncludeReminder}
                  onChange={(e) => setLateSmsIncludeReminder(e.target.checked)}
                  disabled={lateSmsSending}
                />
                <span className="scope-label">
                  תזמן גם תזכורת SMS ({lateSmsReminderHours} שעות לפני{" "}
                  {lateSmsScope === "series" && lateSmsSeriesAvailable
                    ? "כל מפגש"
                    : "הפגישה"}
                  )
                </span>
              </label>
            )}

            <p className="late-sms-note">
              <Info size={14} weight="fill" />
              ההודעה תישלח מיידית למספר המסתיים ב-
              {lateSmsPhone.replace(/\D/g, "").slice(-4)}
            </p>

            <div className="dialog-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setShowLateSmsDialog(false)}
                disabled={lateSmsSending}
              >
                ביטול
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleLateSmsSend}
                disabled={lateSmsSending}
              >
                {lateSmsSending ? (
                  <>
                    <LoaderLoop size="xs" /> שולח...
                  </>
                ) : (
                  "שליחה"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recurring Edit Scope Dialog */}
      {showRecurringEditDialog && (
        <div
          className="modal-overlay recurring-delete-overlay"
          onClick={() => { setShowRecurringEditDialog(false); pendingEditDataRef.current = null; setRowScopeEdit(null); }}
        >
          <div
            className="recurring-delete-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dialog-header">
              <NotePencil size={24} weight="bold" className="dialog-icon" />
              <h3>עריכת מפגש בסדרה</h3>
            </div>
            <p className="dialog-message">
              מפגש זה הוא חלק מסדרת מפגשים. מה ברצונך לעדכן?
            </p>
            <div className="delete-scope-options">
              <label className="scope-option">
                <input
                  type="radio"
                  name="editScope"
                  value="single"
                  checked={editScope === "single"}
                  onChange={(e) => setEditScope(e.target.value)}
                />
                <span className="scope-label">
                  מפגש זה בלבד
                  {scopeNewStart && (
                    <span className="scope-hint">
                      רק מפגש זה יעבור ל{formatScopeWhen(scopeNewStart)} • שאר
                      המפגשים יישארו כפי שהם
                    </span>
                  )}
                </span>
              </label>
              <label className="scope-option">
                <input
                  type="radio"
                  name="editScope"
                  value="future"
                  checked={editScope === "future"}
                  onChange={(e) => setEditScope(e.target.value)}
                />
                <span className="scope-label">
                  מפגש זה וכל המפגשים העתידיים
                  {scopeNewStart && (
                    <span className="scope-hint">
                      כל המפגשים הבאים יעברו ל{formatScopeWhen(scopeNewStart, { withDate: false })}
                      {laterShiftedMeetings.length > 0
                        ? ` • ${laterShiftedMeetings.length} מפגשים נוספים יזוזו`
                        : ""}
                    </span>
                  )}
                </span>
              </label>
              {/* Postponing the rest by a week only makes sense once the
                  meeting actually moved to another day. */}
              {canPostponeSeries && (
                <label className="scope-option">
                  <input
                    type="radio"
                    name="editScope"
                    value="postpone_week"
                    checked={editScope === "postpone_week"}
                    onChange={(e) => setEditScope(e.target.value)}
                  />
                  <span className="scope-label">
                    מפגש זה בלבד, ולדחות את שאר המפגשים בשבוע
                    <span className="scope-hint">
                      מפגש זה יעבור ל{formatScopeWhen(scopeNewStart)}
                      {postponedNextMeetingDate && (
                        <>
                          {" "}
                          • {postponableMeetings.length} המפגשים הבאים יישארו
                          באותו יום ושעה ויידחו בשבוע — הבא יתקיים ב
                          {formatScopeWhen(postponedNextMeetingDate)}
                        </>
                      )}
                      {postponedLastMeetingDate && (
                        <> • הסדרה תסתיים ב-{formatHebrewDateUtil(postponedLastMeetingDate)}</>
                      )}
                    </span>
                  </span>
                </label>
              )}
            </div>
            {editScope === "postpone_week" && (
              <p className="dialog-note">
                שינויים בפרטים אחרים (מחיר, הערות) יחולו על מפגש זה בלבד — בשאר
                המפגשים ישתנה רק התאריך
              </p>
            )}
            <div className="dialog-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => { setShowRecurringEditDialog(false); pendingEditDataRef.current = null; setRowScopeEdit(null); }}
              >
                ביטול
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleRecurringEditConfirm}
                disabled={isUpdatingRecurring}
              >
                {isUpdatingRecurring ? "מעדכן..." : "עדכן"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recurring Delete Dialog — extended to ask about the linked payment
          (for the current appointment) and to let the therapist opt in to
          deleting historical siblings (default: skip past). */}
      {showRecurringDeleteDialog && (() => {
        const isWorking = recurringDeleteState.isWorking || isDeletingRecurring;
        const tx = recurringDeleteState.linkedTransaction;
        const isLoadingTx = tx === undefined;
        const hasTx = !!tx;
        const txIsPaidFamily = hasTx && TX_PAID_FAMILY.has(tx.status);
        const hasInvoice =
          Array.isArray(appointment?.RecepitsDocsRefArray) &&
          appointment.RecepitsDocsRefArray.length > 0;
        const showIncludePastToggle = deleteScope !== "single";
        return (
          <div
            className="modal-overlay recurring-delete-overlay"
            onClick={() => {
              if (!isWorking) setShowRecurringDeleteDialog(false);
            }}
          >
            <div
              className="recurring-delete-dialog"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="dialog-header">
                <Trash size={24} weight="bold" className="dialog-icon" />
                <h3>מחיקת מפגש מסדרה</h3>
              </div>
              <p className="dialog-message">
                מפגש זה הוא חלק מסדרה של {appointment?.recurringTotalCount}{" "}
                מפגשים. מה ברצונך למחוק?
              </p>
              <div className="delete-scope-options">
                <label className="scope-option">
                  <input
                    type="radio"
                    name="deleteScope"
                    value="single"
                    checked={deleteScope === "single"}
                    onChange={(e) => setDeleteScope(e.target.value)}
                    disabled={isWorking}
                  />
                  <span className="scope-label">
                    מפגש זה בלבד
                    <span className="scope-hint">
                      המפגש יוסר מהסדרה לחלוטין ולא ייווצר מפגש חלופי במקומו.
                      שאר המפגשים יישארו במועדים שלהם והסדרה תסתיים באותו תאריך.
                    </span>
                  </span>
                </label>
                <label className="scope-option">
                  <input
                    type="radio"
                    name="deleteScope"
                    value="future"
                    checked={deleteScope === "future"}
                    onChange={(e) => setDeleteScope(e.target.value)}
                    disabled={isWorking}
                  />
                  <span className="scope-label">
                    מפגש זה וכל המפגשים העתידיים
                    {laterSeriesMeetings.length > 0 && (
                      <span className="scope-hint">
                        {`יימחקו ${laterSeriesMeetings.length + 1} מפגשים (כולל מפגש זה). מפגשים שכבר התקיימו יישארו, אלא אם תסמן את האפשרות למטה.`}
                      </span>
                    )}
                  </span>
                </label>
                <label className="scope-option">
                  <input
                    type="radio"
                    name="deleteScope"
                    value="all"
                    checked={deleteScope === "all"}
                    onChange={(e) => setDeleteScope(e.target.value)}
                    disabled={isWorking}
                  />
                  <span className="scope-label">
                    כל המפגשים בסדרה
                    <span className="scope-hint">
                      הסדרה כולה תימחק. מפגשים שכבר התקיימו יישארו, אלא אם תסמן
                      את האפשרות למטה.
                    </span>
                  </span>
                </label>
              </div>

              {/* Deleting is rarely what a therapist actually wants when a
                  single session falls through — offer the postpone instead. */}
              {deleteScope === "single" && canPostponeAppointment && (
                <div className="apt-delete-postpone-alt">
                  <ArrowsClockwise size={18} weight="duotone" />
                  <div>
                    <strong>לא רוצה לאבד את המפגש?</strong>
                    <span>
                      {" "}
                      אפשר לדחות אותו בשבוע
                      {postponableMeetings.length > 0
                        ? ` ולדחות איתו את ${postponableMeetings.length} המפגשים הבאים`
                        : ""}
                      , כך שהסדרה תישמר במלואה.
                    </span>
                    <button
                      type="button"
                      className="btn btn-secondary apt-delete-postpone-btn"
                      onClick={() => {
                        setShowRecurringDeleteDialog(false);
                        startPostpone(appointment);
                      }}
                      disabled={isWorking}
                    >
                      <CalendarPlus size={16} weight="duotone" />
                      דחה בשבוע במקום למחוק
                    </button>
                  </div>
                </div>
              )}

              {/* Include-past toggle — only relevant when scope > single. */}
              {showIncludePastToggle && (
                <label className="apt-delete-include-past">
                  <input
                    type="checkbox"
                    checked={recurringDeleteState.includePast}
                    onChange={(e) =>
                      setRecurringDeleteState((prev) => ({
                        ...prev,
                        includePast: e.target.checked,
                      }))
                    }
                    disabled={isWorking}
                  />
                  <span>
                    מחק גם מפגשים שכבר התקיימו בסדרה
                    <span className="apt-delete-hint">
                      {" "}
                      (כברירת מחדל מפגשים שעברו אינם נמחקים)
                    </span>
                  </span>
                </label>
              )}

              {/* Linked-payment block for the current appointment. */}
              <div className="apt-delete-payment-block">
                {isLoadingTx && (
                  <div className="apt-delete-payment-loading">
                    <LoaderLoop size="xs" />
                    <span>בודק תיעוד תשלום למפגש זה…</span>
                  </div>
                )}
                {!isLoadingTx && hasTx && (
                  <>
                    <div
                      className={`apt-delete-payment-card ${
                        txIsPaidFamily ? "paid" : "pending"
                      }`}
                    >
                      <CurrencyCircleDollar size={20} weight="duotone" />
                      <div className="apt-delete-payment-text">
                        <strong>למפגש זה קיים תיעוד תשלום</strong>
                        <span>
                          סטטוס: {describeTxStatus(tx)}
                          {tx.amount ? ` · ${formatTxAmount(tx.amount)}` : ""}
                        </span>
                      </div>
                    </div>
                    <label className="apt-delete-payment-checkbox">
                      <input
                        type="checkbox"
                        checked={recurringDeleteState.deleteLinkedTransactions}
                        onChange={(e) =>
                          setRecurringDeleteState((prev) => ({
                            ...prev,
                            deleteLinkedTransactions: e.target.checked,
                          }))
                        }
                        disabled={isWorking}
                      />
                      <span>
                        מחק גם את תיעוד התשלום של מפגשים שיימחקו
                        <span className="apt-delete-hint">
                          {" "}
                          (אם לא תסומן — התיעוד יישאר ויסומן כ"בוטל")
                        </span>
                      </span>
                    </label>
                    {hasInvoice && (
                      <div className="apt-delete-invoice-warning">
                        <Receipt size={18} weight="duotone" />
                        <div>
                          <strong>הופקה חשבונית עבור מפגש זה.</strong>
                          <span>
                            {" "}
                            מחיקת התיעוד כאן לא מבטלת את החשבונית הרשמית
                            במערכת ספק החשבוניות. לזיכוי / ביטול חשבונית יש
                            לפעול ישירות מול הספק.
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="dialog-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowRecurringDeleteDialog(false)}
                  disabled={isWorking}
                >
                  ביטול
                </button>
                <button
                  type="button"
                  className="recurring-delete-confirm-btn"
                  onClick={handleRecurringDeleteConfirm}
                  disabled={isWorking || isLoadingTx}
                >
                  {isWorking ? "מוחק..." : "מחק"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* SMS Preview Overlay */}
      {showSmsPreview && (
        <div className="modal-overlay sms-preview-overlay" onClick={() => setShowSmsPreview(false)}>
          <div className="sms-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sms-preview-header">
              <button className="sms-preview-back" onClick={() => setShowSmsPreview(false)}>
                <ArrowRight size={18} weight="bold" />
                <span>חזרה</span>
              </button>
              <h3>תצוגה מקדימה</h3>
              <div style={{ width: 80 }} />
            </div>

            <div className="sms-preview-body">
              {/* Appointment Summary */}
              <div className="sms-preview-summary">
                <div className="sms-preview-summary-title">פרטי הפגישה</div>
                <div className="sms-preview-summary-row">
                  <User size={18} weight="fill" />
                  <span>{formData.patientName}</span>
                </div>
                <div className="sms-preview-summary-row">
                  <Calendar size={18} weight="fill" />
                  <span>{formatHebrewDate(formData.date)}</span>
                </div>
                <div className="sms-preview-summary-row">
                  <Clock size={18} weight="fill" />
                  <span>{formData.startTime} ({formData.duration} דק׳)</span>
                </div>
                {formData.isRecurring && (
                  <div className="sms-preview-summary-row">
                    <Repeat size={18} weight="fill" />
                    <span>סדרת פגישות - {getFrequencyLabel(formData.recurringFrequency)} ({formData.seriesTotalCount} פגישות)</span>
                  </div>
                )}
                {canAccessFinance && formData.paymentAmount && (
                  <div className="sms-preview-summary-row">
                    <CurrencyCircleDollar size={18} weight="fill" />
                    <span>₪{formData.paymentAmount}</span>
                  </div>
                )}
              </div>

              {/* SMS SECTION */}
              {smsPreviewItems.length === 0 ? (
                <div className="sms-preview-empty">
                  <ChatCircleText size={20} weight="fill" />
                  <span>
                    {isPastDateTime
                      ? "לא יישלחו הודעות SMS לפגישה שכבר עברה."
                      : "לא ניתן לשלוח SMS — למטופל/ת אין מספר טלפון."}
                  </span>
                </div>
              ) : (() => {
                const enabledCount = smsPreviewItems.filter((i) => i.enabled).length;
                const suppressedCount = smsPreviewItems.filter((i) => i.suppressedReason).length;
                const masterOn = enabledCount > 0;
                return (
                  <div className="sms-preview-sms-section">
                    {/* Master opt-in toggle (synced with the form's "send SMS" choice) */}
                    <label className={`sms-master-toggle ${masterOn ? "on" : ""}`}>
                      <input
                        type="checkbox"
                        checked={masterOn}
                        onChange={(e) => setAllSmsEnabled(e.target.checked)}
                      />
                      <span className="sms-master-toggle-check">
                        {masterOn && <Check size={15} weight="bold" />}
                      </span>
                      <span className="sms-master-toggle-text">
                        <span className="sms-master-toggle-title">שליחת הודעות SMS למטופל/ת</span>
                        <span className="sms-master-toggle-sub">
                          {masterOn
                            ? `${enabledCount} ${enabledCount === 1 ? "הודעה תישלח" : "הודעות יישלחו"} אוטומטית`
                            : "לא יישלחו הודעות. ניתן להפעיל בכל עת."}
                        </span>
                      </span>
                    </label>

                    {/* Why fewer messages than expected will go out */}
                    {suppressedCount > 0 && (
                      <div className="sms-preview-suppressed-note sms-preview-suppressed-summary">
                        <Warning size={15} weight="fill" />
                        <span>
                          {suppressedCount === 1
                            ? "הודעה אחת לא תישלח בגלל מועד השליחה. פירוט בהתאמת ההודעות."
                            : `${suppressedCount} הודעות לא יישלחו בגלל מועד השליחה. פירוט בהתאמת ההודעות.`}
                        </span>
                      </div>
                    )}

                    {/* Collapsed-by-default advanced controls */}
                    {(masterOn || suppressedCount > 0) && (
                      <div className="sms-advanced">
                        <button
                          type="button"
                          className={`sms-advanced-toggle ${showSmsDetails ? "open" : ""}`}
                          onClick={() => setShowSmsDetails((v) => !v)}
                        >
                          <SlidersHorizontal size={16} weight="bold" />
                          <span>התאמת הודעות אישית</span>
                          <span className="sms-advanced-count">{enabledCount}/{smsPreviewItems.length}</span>
                          <CaretDown size={16} weight="bold" className="sms-advanced-caret" />
                        </button>

                        {showSmsDetails && (
                          <div className="sms-preview-list">
                            {smsPreviewItems.map((item) => (
                              <div key={item.id} className={`sms-preview-item ${item.enabled ? "" : "disabled"} ${item.suppressedReason ? "suppressed" : ""}`}>
                                <div className="sms-preview-item-top">
                                  <label className="sms-preview-checkbox" onClick={(e) => e.stopPropagation()}>
                                    <input
                                      type="checkbox"
                                      checked={item.enabled}
                                      onChange={() => toggleSmsPreviewItem(item.id)}
                                    />
                                    <span className="sms-preview-item-titles">
                                      <span className="sms-preview-item-label">{item.label}</span>
                                      {item.type !== "series" && (
                                        <span className="sms-preview-item-apt">
                                          {formatHebrewDate(item.appointmentDate)} · {item.appointmentTime}
                                        </span>
                                      )}
                                    </span>
                                  </label>
                                  <span className={`sms-preview-type-badge ${item.type}`}>
                                    {item.type === "series" ? "סדרה" : item.type === "initial" ? "אישור" : "תזכורת"}
                                  </span>
                                </div>

                                {item.suppressedReason && (
                                  <div className="sms-preview-suppressed-note">
                                    <Warning size={15} weight="fill" />
                                    <span>{item.suppressedReason}</span>
                                  </div>
                                )}

                                {(item.enabled || item.suppressedReason) && (
                                  <>
                                    <div className="sms-preview-when">
                                      <span className="sms-preview-when-label">
                                        <PaperPlaneTilt size={14} weight="fill" />
                                        מועד שליחה
                                        {item.isImmediate && <span className="sms-preview-immediate-badge">מיידי</span>}
                                      </span>
                                      <div className="sms-preview-datetime">
                                        <DatePicker
                                          value={item.scheduledDate}
                                          onChange={(e) => updateSmsPreviewItem(item.id, "date", e.target.value)}
                                        />
                                        <TimePicker
                                          value={item.scheduledTime}
                                          onChange={(e) => updateSmsPreviewItem(item.id, "time", e.target.value)}
                                        />
                                        {item.scheduledDayName && (
                                          <span className="sms-preview-day-badge">{item.scheduledDayName}</span>
                                        )}
                                      </div>
                                    </div>

                                    {item.enabled && item.treatmentTypeOmitted && (
                                      <div className="sms-preview-treatment-warning">
                                        <span>⚠️ סוג הטיפול הושמט מההודעה כי חריגה מ-201 תווים</span>
                                      </div>
                                    )}

                                    {item.enabled && (
                                      <div className="sms-preview-bubble">
                                        <p>{item.message}</p>
                                        <span className="sms-preview-link-placeholder">🔗 קישור לאישור הגעה</span>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            <div className="sms-preview-footer">
              <CustomClinikitActionButton
                variant="secondary"
                type="button"
                onClick={() => setShowSmsPreview(false)}
              >
                חזרה לעריכה
              </CustomClinikitActionButton>
              <CustomClinikitActionButton
                type="button"
                icon={<Check size={16} weight="bold" />}
                onClick={(e) => { setShowSmsPreview(false); handleSubmit(e); }}
                disabled={isCreating || isCreatingRecurring}
                loading={isCreating || isCreatingRecurring}
              >
                {isCreating || isCreatingRecurring ? "יוצר..." : "צור פגישה"}
              </CustomClinikitActionButton>
            </div>
          </div>
        </div>
      )}

    </div>

    {/* Shared cancel-appointment flow modal (footer "בטל פגישה") */}
    {cancelFlowNode}

    {/* Lightweight cancel prompt used by the series overview table's per-row X.
        Collects only an optional reason — skips the full fee / invoice flow
        because bulk-cancelling a series occurrence from the overview is
        expected to be a fast action. */}
    {seriesCancelPrompt.open && (
      <div
        className="cancel-flow-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) handleSeriesCancelDismiss();
        }}
      >
        <div
          className="cancel-flow-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <ModalCloseButton
            onClick={(e) => {
              e.stopPropagation();
              handleSeriesCancelDismiss();
            }}
          />
          <div className="cancel-flow-step">
            <div className="cancel-flow-icon warning">
              <CalendarX size={28} weight="duotone" />
            </div>
            <h3 className="cancel-flow-title">סימון מפגש כבוטל</h3>
            <p className="cancel-flow-message">
              המפגש יסומן כ"בוטלה" בסדרה. ניתן להוסיף סיבה (אופציונאלי).
            </p>
            <div className="cancel-flow-reason">
              <div className="cancel-flow-reason-body">
                <textarea
                  className="cancel-flow-reason-input"
                  placeholder="לדוגמה: המטופל/ת ביטל/ה, חופשה, מחלה…"
                  value={seriesCancelPrompt.reason}
                  maxLength={300}
                  rows={3}
                  onChange={(e) =>
                    setSeriesCancelPrompt((prev) => ({
                      ...prev,
                      reason: e.target.value.slice(0, 300),
                    }))
                  }
                  disabled={seriesCancelPrompt.isWorking}
                  autoFocus
                />
                <div className="cancel-flow-reason-footer">
                  <span className="cancel-flow-reason-hint">
                    הסיבה תוצג בכרטיס הפגישה וביומן המטופל.
                  </span>
                  <span className="cancel-flow-reason-count">
                    {(seriesCancelPrompt.reason || "").length}/300
                  </span>
                </div>
              </div>
            </div>
            <div className="cancel-flow-actions">
              <button
                type="button"
                className="cancel-flow-btn secondary"
                onClick={handleSeriesCancelDismiss}
                disabled={seriesCancelPrompt.isWorking}
              >
                חזרה
              </button>
              <button
                type="button"
                className="cancel-flow-btn primary warning"
                onClick={handleSeriesCancelConfirm}
                disabled={seriesCancelPrompt.isWorking}
              >
                {seriesCancelPrompt.isWorking ? "מסמן..." : "סמן כבוטל"}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

export default AppointmentModal;
